/**
 * UltraLife Trades — Market Configurations
 *
 * One entry per metro market. Each market defines:
 *  - slug:            URL-safe key  (/boise/decks or ?market=boise)
 *  - displayName:     Human-readable city name
 *  - state:           Two-letter state code
 *  - county:          Primary county / jurisdiction
 *  - licenseNote:     State contractor licensing note
 *  - trades:          Which trades are active in this market
 *  - serviceArea:     City list shown to visitors
 *  - buildingCodes:   Per-trade code notes injected into AI prompts
 *  - pricingNotes:    Local cost-of-labor / material adjustments for AI
 *  - geo:             Approximate lat/lng for geolocation matching
 */

export const MARKETS = {
  // ─── IDAHO ───
  boise: {
    slug: "boise",
    region: "id-wy-intermountain",
    terrain: "mountain-west",
    displayName: "Boise",
    state: "ID",
    county: "Ada County",
    licenseNote: "Idaho has no statewide GC license requirement. Local permits required.",
    trades: ["decks", "concrete", "painting"], // roofing removed: license-required cell (hq#19)
    serviceArea: ["Boise", "Meridian", "Nampa", "Caldwell", "Eagle", "Star", "Kuna", "Garden City"],
    buildingCodes: {
      decks: "Ada County: Decks ≥30\" high or >200 sqft require permits. Snow loads 25-50 psf depending on elevation. Frost depth 24\". IRC adopted. No TRPA-style environmental overlay.",
      concrete: "Standard IBC. Freeze-thaw rated mix required (min 4000 psi, air-entrained). Subbase prep critical for Treasure Valley clay soils.",
      painting: "No special permits for painting. VOC regulations apply for commercial work.",
      roofing: "Ada County requires permits for full tear-offs. Wind zone: 90 mph. Class A fire rating required in WUI areas (Foothills).",
    },
    pricingNotes: "Mid-market labor costs. Material prices comparable to national averages. Active growth market — material lead times can extend in peak season.",
    geo: { lat: 43.615, lng: -116.202 },
  },

  // ─── COLORADO ───
  colorado_springs: {
    slug: "colorado-springs",
    region: "co-rocky-mountain",
    terrain: "mountain-west",
    displayName: "Colorado Springs",
    state: "CO",
    county: "El Paso County",
    licenseNote: "Colorado has no statewide GC license. El Paso County permits required.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Colorado Springs", "Manitou Springs", "Monument", "Fountain", "Security-Widefield", "Peyton"],
    buildingCodes: {
      decks: "El Paso County: 30 psf ground snow load. Decks over 30\" require permit and inspections. Wildfire risk — check WUI code for fire-resistant materials near Pikes Peak area. IRC 2021 adopted.",
      concrete: "High-altitude, freeze-thaw climate. 4000 psi air-entrained minimum. Control joints every 10 ft. Radon-resistant sub-slab in some zones.",
      painting: "No permits for residential painting. High UV at altitude — premium exterior paints recommended.",
      roofing: "El Paso County: hail zone. Class 4 impact-resistant shingles recommended — significant insurance discount. Wind: 90-115 mph. WUI fire-resistant ratings in mountain communities.",
    },
    pricingNotes: "Competitive labor market. Hail damage work creates periodic demand spikes for roofing and painting.",
    geo: { lat: 38.833, lng: -104.821 },
  },

  fort_collins: {
    slug: "fort-collins",
    region: "co-rocky-mountain",
    terrain: "mountain-west",
    displayName: "Fort Collins",
    state: "CO",
    county: "Larimer County",
    licenseNote: "Colorado has no statewide GC license. City of Fort Collins / Larimer County permits required.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Fort Collins", "Loveland", "Windsor", "Timnath", "Wellington", "Greeley"],
    buildingCodes: {
      decks: "Larimer County: 30-40 psf snow load (higher in foothills). City of Fort Collins has its own inspection office. Frost depth 30\". Engineered plans typically required for multi-level or elevated decks.",
      concrete: "Front Range freeze-thaw: 4000 psi air-entrained. Expansive soils common — proper subbase prep essential.",
      painting: "High UV environment. Low-e / high-reflectance coatings recommended for west-facing elevations.",
      roofing: "Larimer County: hail-prone. Impact-resistant Class 4 shingles often required by HOAs. Wind 90+ mph. CSU area has urban fire codes.",
    },
    pricingNotes: "College town with strong rental renovation demand. Foothills work commands premium.",
    geo: { lat: 40.585, lng: -105.084 },
  },

  // ─── OHIO ───
  columbus: {
    slug: "columbus",
    region: "oh-ky-in-rust-belt",
    terrain: "midwest",
    displayName: "Columbus",
    state: "OH",
    county: "Franklin County",
    licenseNote: "Ohio has no statewide GC license. Columbus / Franklin County permits required.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Columbus", "Dublin", "Westerville", "Gahanna", "Hilliard", "Grove City", "Newark", "Lancaster"],
    buildingCodes: {
      decks: "Franklin County / City of Columbus: 20-25 psf snow load. Decks over 200 sqft or >30\" above grade require permit. Frost depth 30\". IRC 2017 adopted with local amendments.",
      concrete: "Moderate freeze-thaw. 3500-4000 psi mix. Clay-heavy soils — proper base and drainage critical. Radon mitigation standard in new construction.",
      painting: "No permits for residential painting. Lead paint disclosure required for pre-1978 homes.",
      roofing: "Franklin County: standard wind zone 90 mph. Re-roofing permits required for full tear-offs. Ice & water shield at eaves.",
    },
    pricingNotes: "Strong growth market. Mid-market labor. High volume of residential renovation from rapid suburb expansion.",
    geo: { lat: 39.961, lng: -82.998 },
  },

  cincinnati: {
    slug: "cincinnati",
    region: "oh-ky-in-rust-belt",
    terrain: "midwest",
    displayName: "Cincinnati",
    state: "OH",
    county: "Hamilton County",
    licenseNote: "Ohio has no statewide GC license. Hamilton County / Cincinnati permits required.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Cincinnati", "Loveland", "Mason", "Blue Ash", "Norwood", "Sharonville", "Florence KY", "Covington KY"],
    buildingCodes: {
      decks: "Hamilton County: 15-20 psf snow load. Standard IRC deck codes. Permit required for decks over 30\" or 200 sqft. Frost depth 24\". Many Cincinnati neighborhoods have historic preservation considerations.",
      concrete: "Mild freeze-thaw. 3500 psi minimum. Hillside lots common — drainage slope essential.",
      painting: "Historic districts (Hyde Park, Mt. Lookout) may require color palette approval.",
      roofing: "Hamilton County: wind 90 mph. Cincinnati has significant tree canopy — debris damage common. Tornado risk — proper fastening codes apply.",
    },
    pricingNotes: "Tri-state market (OH/KY/IN). Competitive labor. Strong historic renovation market in older neighborhoods.",
    geo: { lat: 39.103, lng: -84.512 },
  },

  // ─── IOWA ───
  des_moines: {
    slug: "des-moines",
    region: "ia-mo-heartland",
    terrain: "midwest",
    displayName: "Des Moines",
    state: "IA",
    county: "Polk County",
    licenseNote: "Iowa has no statewide GC license. City of Des Moines / Polk County permits required.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Des Moines", "West Des Moines", "Ankeny", "Urbandale", "Waukee", "Clive", "Johnston"],
    buildingCodes: {
      decks: "Polk County: 25-30 psf snow load. Frost depth 42\". Permit required for decks >200 sqft or >30\" high. IBC/IRC adopted. Strong wind zone — proper post anchoring required.",
      concrete: "Deep freeze. 4000 psi air-entrained minimum. Frost depth 42\" means deep footings. Expansive clay soils — gravel base essential.",
      painting: "No permits for residential painting. Lead paint disclosure for pre-1978.",
      roofing: "Polk County: tornado/hail zone. Class 4 impact resistant strongly recommended. Wind 90-100 mph. Ice & water shield at eaves standard.",
    },
    pricingNotes: "Strong Midwest value market. Farm-to-suburb growth driving renovation demand. Lower labor costs than coastal markets.",
    geo: { lat: 41.586, lng: -93.625 },
  },

  // ─── NEBRASKA ───
  omaha: {
    slug: "omaha",
    region: "ks-ne-plains",
    terrain: "midwest",
    displayName: "Omaha",
    state: "NE",
    county: "Douglas / Sarpy County",
    licenseNote: "Nebraska has no statewide GC license. City of Omaha / county permits required.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Omaha", "Bellevue", "Papillion", "La Vista", "Ralston", "Millard", "Council Bluffs IA"],
    buildingCodes: {
      decks: "Douglas County: 20 psf snow load. Frost depth 36-42\". Permit required. IRC adopted. Tornado/wind zone: proper ledger bolting and post anchoring required.",
      concrete: "Moderate-to-heavy freeze-thaw. 4000 psi air-entrained. Sarpy County expansive soils — 4\" compacted gravel base minimum.",
      painting: "No permits for residential painting.",
      roofing: "Omaha Metro: active hail and tornado zone. Impact-resistant roofing pays for itself in insurance savings. Wind 90-110 mph.",
    },
    pricingNotes: "Affordable Midwest market. Strong demand from new construction suburbs (Papillion, La Vista) and older home renovations in Omaha proper.",
    geo: { lat: 41.257, lng: -95.995 },
  },

  lincoln: {
    slug: "lincoln",
    region: "ks-ne-plains",
    terrain: "midwest",
    displayName: "Lincoln",
    state: "NE",
    county: "Lancaster County",
    licenseNote: "Nebraska has no statewide GC license. City of Lincoln permits required.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Lincoln", "Waverly", "Hickman", "Crete", "Seward", "Beatrice"],
    buildingCodes: {
      decks: "Lancaster County: 20 psf snow load. Frost depth 36\". City of Lincoln has active permit and inspection program. Deck permits required for >200 sqft.",
      concrete: "Freeze-thaw climate. 4000 psi air-entrained. Lincoln expansive clay soils are notorious — crushed rock base essential.",
      painting: "No permits. UNL area has large older housing stock with lead paint.",
      roofing: "Lancaster County: hail and tornado zone. Class 4 impact roofing recommended. Wind 90-105 mph.",
    },
    pricingNotes: "College town (UNL) with steady rental renovation market. Competitive labor pricing.",
    geo: { lat: 40.813, lng: -96.702 },
  },

  // ─── INDIANA ───
  indianapolis: {
    slug: "indianapolis",
    region: "oh-ky-in-rust-belt",
    terrain: "midwest",
    displayName: "Indianapolis",
    state: "IN",
    county: "Marion County (Consolidated City-County)",
    licenseNote: "Indiana has no statewide GC license. Indianapolis / Marion County permits required.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Indianapolis", "Carmel", "Fishers", "Noblesville", "Greenwood", "Avon", "Brownsburg", "Zionsville"],
    buildingCodes: {
      decks: "Marion County / Indy: 20-25 psf snow load. Frost depth 30\". Standard IRC deck permits required. Hamilton County suburbs may have additional HOA requirements.",
      concrete: "Moderate freeze-thaw. 3500-4000 psi. Flat terrain with poor drainage — slope and French drain planning important.",
      painting: "No permits for residential painting. IDEM VOC regulations for commercial.",
      roofing: "Indiana tornado alley. Wind 90-100 mph. Hail damage common. Ice & water shield at eaves.",
    },
    pricingNotes: "Strong Indianapolis metro growth. North suburbs (Carmel, Fishers) command premium. Cost-effective labor overall.",
    geo: { lat: 39.768, lng: -86.158 },
  },

  fort_wayne: {
    slug: "fort-wayne",
    region: "oh-ky-in-rust-belt",
    terrain: "midwest",
    displayName: "Fort Wayne",
    state: "IN",
    county: "Allen County",
    licenseNote: "Indiana has no statewide GC license. Allen County / City of Fort Wayne permits required.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Fort Wayne", "New Haven", "Leo-Cedarville", "Huntertown", "Grabill", "Auburn"],
    buildingCodes: {
      decks: "Allen County: 25-30 psf snow load (lake effect influence). Frost depth 36\". Permit required for decks >30\" or >200 sqft.",
      concrete: "Heavier freeze-thaw than southern IN. 4000 psi air-entrained. Clay soils with drainage issues.",
      painting: "No permits. Fort Wayne has significant older housing stock.",
      roofing: "Allen County: lake-effect snow and hail. Ice & water shield standard. Wind 90 mph.",
    },
    pricingNotes: "Secondary market with low cost of labor. Strong demand from manufacturing sector workers.",
    geo: { lat: 41.079, lng: -85.139 },
  },

  // ─── KANSAS ───
  kansas_city_ks: {
    slug: "kansas-city-ks",
    region: "ks-ne-plains",
    terrain: "midwest",
    displayName: "Kansas City (KS)",
    state: "KS",
    county: "Wyandotte County",
    licenseNote: "Kansas has no statewide GC license. Wyandotte County / KCK permits required.",
    trades: ["decks", "concrete", "painting"], // roofing removed: license-required cell (hq#19)
    serviceArea: ["Kansas City KS", "Overland Park", "Olathe", "Shawnee", "Lenexa", "Merriam", "Prairie Village"],
    buildingCodes: {
      decks: "Wyandotte / Johnson County: 20-25 psf snow load. Frost depth 36\". Kansas City metro straddles KS/MO — verify jurisdiction by address. Johnson County (Overland Park) is especially permit-active.",
      concrete: "Moderate freeze-thaw. 4000 psi air-entrained. Expansive clay soils — 4\" gravel base + control joints.",
      painting: "No permits for residential painting.",
      roofing: "Tornado alley! Wind 90-110 mph. Class 4 hail-resistant roofing standard in insurance market. Storm chasers active in this market — vet all roofing leads carefully.",
    },
    pricingNotes: "Strong JoCo (Johnson County) premium market. Tornado/hail damage creates roofing demand spikes. Competitive overall.",
    geo: { lat: 39.114, lng: -94.627 },
  },

  wichita: {
    slug: "wichita",
    region: "ks-ne-plains",
    terrain: "south-central",
    displayName: "Wichita",
    state: "KS",
    county: "Sedgwick County",
    licenseNote: "Kansas has no statewide GC license. City of Wichita / Sedgwick County permits required.",
    trades: ["decks", "concrete", "painting"], // roofing removed: license-required cell (hq#19)
    serviceArea: ["Wichita", "Andover", "Derby", "Haysville", "Newton", "El Dorado", "Augusta"],
    buildingCodes: {
      decks: "Sedgwick County: 20 psf snow load. Frost depth 30-36\". City of Wichita permit required for decks >200 sqft.",
      concrete: "Moderate freeze-thaw. 4000 psi. Expansive soils — proper base prep critical.",
      painting: "No permits. Wichita has large inventory of mid-century homes.",
      roofing: "Tornado alley. Wind 90-100 mph. Hail damage endemic. Class 4 impact resistant recommended.",
    },
    pricingNotes: "Mid-market. Aerospace sector employment drives stable renovation demand. Periodic hail events create roofing spikes.",
    geo: { lat: 37.687, lng: -97.330 },
  },

  // ─── KENTUCKY ───
  louisville: {
    slug: "louisville",
    region: "oh-ky-in-rust-belt",
    terrain: "south-central",
    displayName: "Louisville",
    state: "KY",
    county: "Jefferson County (Louisville Metro)",
    licenseNote: "Kentucky has no statewide GC license for most trades. Louisville Metro permits required.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Louisville", "Jeffersontown", "St. Matthews", "Shively", "Elizabethtown", "La Grange", "New Albany IN"],
    buildingCodes: {
      decks: "Louisville Metro: 15-20 psf snow load. Frost depth 18-24\". Permit required for decks >30\" or attached to structure. Louisville historic districts (Cherokee Triangle, Crescent Hill) may have design review.",
      concrete: "Mild freeze-thaw. 3500 psi min. Good drainage essential in Ohio River basin.",
      painting: "Louisville Metro has historic district color standards in Old Louisville, Cherokee Triangle.",
      roofing: "Jefferson County: Wind 90 mph. Tornado risk. Ice & water shield at eaves in cold roof zones.",
    },
    pricingNotes: "Strong bourbon/tourism economy. Old Louisville has high-value historic renovation market. NuLu area driving commercial painting demand.",
    geo: { lat: 38.252, lng: -85.758 },
  },

  lexington: {
    slug: "lexington",
    region: "oh-ky-in-rust-belt",
    terrain: "south-central",
    displayName: "Lexington",
    state: "KY",
    county: "Fayette County",
    licenseNote: "Kentucky has no statewide GC license for most trades. Lexington-Fayette Urban County permits required.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Lexington", "Nicholasville", "Richmond", "Winchester", "Georgetown", "Paris"],
    buildingCodes: {
      decks: "Fayette County: 15-20 psf snow load. Frost depth 18\". Standard IRC deck permits. Horse farm properties may have rural county codes.",
      concrete: "Mild climate. 3500 psi minimum. Limestone karst geology — check for sinkhole risk in some areas.",
      painting: "No permits. University of Kentucky area has significant rental renovation market.",
      roofing: "Fayette County: Wind 90 mph. Tornado risk moderate. Standard IRC roofing codes.",
    },
    pricingNotes: "University town with steady demand. Bluegrass horse country estates command premium pricing.",
    geo: { lat: 38.040, lng: -84.503 },
  },

  // ─── MISSOURI ───
  springfield_mo: {
    slug: "springfield-mo",
    region: "ia-mo-heartland",
    terrain: "midwest",
    displayName: "Springfield MO",
    state: "MO",
    county: "Greene County",
    licenseNote: "Missouri has no statewide GC license. City of Springfield / Greene County permits required.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Springfield", "Nixa", "Ozark", "Republic", "Battlefield", "Rogersville", "Joplin"],
    buildingCodes: {
      decks: "Greene County: 20 psf snow load. Frost depth 24\". Standard IRC deck permits. Ozark hillside lots common — engineered footings may be required for steep terrain.",
      concrete: "Moderate freeze-thaw. 4000 psi. Ozark limestone substrate — check for bedrock depth.",
      painting: "No permits for residential painting.",
      roofing: "Tornado alley. Wind 90-100 mph. Hail active. Class 4 impact resistant strongly recommended.",
    },
    pricingNotes: "Secondary Ozarks market. Bass Pro / healthcare sector employment. Budget-conscious consumer base with growing affluent suburbs.",
    geo: { lat: 37.215, lng: -93.298 },
  },

  kansas_city_mo: {
    slug: "kansas-city-mo",
    region: "ia-mo-heartland",
    terrain: "midwest",
    displayName: "Kansas City MO",
    state: "MO",
    county: "Jackson / Clay County",
    licenseNote: "Missouri has no statewide GC license. Kansas City / county permits required.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Kansas City MO", "Lee's Summit", "Independence", "Blue Springs", "Liberty", "Gladstone", "Raytown", "North KC"],
    buildingCodes: {
      decks: "Jackson/Clay County: 20-25 psf snow load. Frost depth 36\". Permits required for decks >30\" above grade. KC Metro straddles KS/MO — confirm address jurisdiction.",
      concrete: "Heavy freeze-thaw. 4000 psi air-entrained. Expansive soils. Control joints standard.",
      painting: "No permits. Historic Westside, Brookside, and Country Club Plaza areas may have preservation guidelines.",
      roofing: "Tornado alley. Wind 90-110 mph. Hail endemic. Class 4 roofing strongly recommended.",
    },
    pricingNotes: "Large metro. Lee's Summit, Blue Springs suburbs growing fast. Tornado/hail damage drives roofing demand peaks.",
    geo: { lat: 39.099, lng: -94.578 },
  },

  // ─── MAINE ───
  portland_me: {
    slug: "portland-me",
    region: "northeast-coastal",
    terrain: "northeast",
    displayName: "Portland ME",
    state: "ME",
    county: "Cumberland County",
    licenseNote: "Maine has no statewide GC license for residential work under $3,000. Cumberland County / City of Portland permits required.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Portland", "South Portland", "Scarborough", "Cape Elizabeth", "Westbrook", "Falmouth", "Yarmouth", "Brunswick"],
    buildingCodes: {
      decks: "Cumberland County: 50-80 psf ground snow load (coastal New England). Frost depth 48\". ALL decks require engineered plans for elevated designs. Coastal salt air requires corrosion-resistant fasteners. Cape and island properties have strict impervious surface limits.",
      concrete: "Heavy freeze-thaw coastal environment. 4500 psi air-entrained minimum. W/C ratio ≤0.40. Deicing salt exposure — use Type V cement or GFRC. Deep frost depth means expensive footings.",
      painting: "Historic Portland districts (West End, Munjoy Hill, Congress Street) have some preservation guidelines. Salt air requires premium exterior coatings.",
      roofing: "Maine wind and snow. 50+ psf snow loads. Ice dam prevention critical — proper insulation, ventilation, and ice & water shield full coverage.",
    },
    pricingNotes: "Premium New England market. Short build season (May-Oct). Material costs elevated. Strong tourist/seasonal property renovation market.",
    geo: { lat: 43.661, lng: -70.255 },
  },

  // ─── NEW HAMPSHIRE ───
  manchester_nh: {
    slug: "manchester-nh",
    region: "northeast-coastal",
    terrain: "northeast",
    displayName: "Manchester NH",
    state: "NH",
    county: "Hillsborough County",
    licenseNote: "New Hampshire has no statewide GC license for residential work under $25,000. Local permits required.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Manchester", "Nashua", "Concord", "Derry", "Bedford", "Goffstown", "Hooksett", "Merrimack"],
    buildingCodes: {
      decks: "Hillsborough County: 50-70 psf ground snow load. Frost depth 48-60\". Engineered plans required for elevated decks in most jurisdictions. Granite State soils often require blasting for footings. Corrosion-resistant hardware required.",
      concrete: "Heavy freeze-thaw. 4500 psi air-entrained minimum. Granite bedrock close to surface in many areas. Frost depth 48\"+ means significant footing cost.",
      painting: "No permits. Short exterior painting season. Manchester historic downtown has some preservation guidelines.",
      roofing: "NH heavy snow and ice. Full ice & water shield or equivalent. Proper ventilation critical for ice dam prevention. Wind 90 mph.",
    },
    pricingNotes: "Strong New Hampshire market — no state income tax drives affluent homeownership. Short season commands premium rates.",
    geo: { lat: 42.995, lng: -71.455 },
  },

  // ─── NEW YORK ───
  rochester_ny: {
    slug: "rochester-ny",
    region: "ny-upstate",
    terrain: "northeast",
    displayName: "Rochester NY",
    state: "NY",
    county: "Monroe County",
    licenseNote: "New York has no statewide GC license (city/county level only). Monroe County / City of Rochester permits required.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Rochester", "Greece", "Irondequoit", "Brighton", "Pittsford", "Fairport", "Webster", "Henrietta"],
    buildingCodes: {
      decks: "Monroe County: 40-60 psf snow load (lake effect). Frost depth 42-48\". Permit required for decks >144 sqft or >30\" high. Engineered plans for elevated/complex designs. Pittsford and other suburbs have active HOA communities.",
      concrete: "Heavy lake-effect freeze-thaw. 4500 psi air-entrained. Chloride-resistant. Salt used heavily on driveways — accelerated deterioration risk if improper mix.",
      painting: "No permits. Rochester has extensive Victorian housing stock with lead paint.",
      roofing: "Monroe County: heavy lake-effect snow. 40-60 psf design load. Full ice & water shield. Hip roofs preferred for snow shedding. Wind 90 mph.",
    },
    pricingNotes: "Rust Belt market with strong renovation demand. Older housing stock drives painting and roofing work. Lake-effect season limits outdoor work Nov-Mar.",
    geo: { lat: 43.157, lng: -77.616 },
  },

  buffalo_ny: {
    slug: "buffalo-ny",
    region: "ny-upstate",
    terrain: "northeast",
    displayName: "Buffalo NY",
    state: "NY",
    county: "Erie County",
    licenseNote: "New York has no statewide GC license. Erie County / City of Buffalo permits required.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Buffalo", "Amherst", "Tonawanda", "Cheektowaga", "Williamsville", "Orchard Park", "Hamburg", "Lancaster"],
    buildingCodes: {
      decks: "Erie County: 50-75 psf snow load (heavy lake effect). Frost depth 48\". ALL elevated decks require engineered plans. Permit required. Corrosion-resistant hardware mandatory. Short outdoor build season.",
      concrete: "Among the toughest freeze-thaw environments in the US. 4500 psi minimum. Air entrainment mandatory. W/C ≤0.40. Salt-resistant mixes for driveways.",
      painting: "Short exterior season (May-Sept). Buffalo has significant historic housing stock. Lead paint common in pre-1978 homes.",
      roofing: "Buffalo: among highest snow loads in continental US. Full structural snow load calculations required. Ice & water shield full coverage. Metal roofing popular for snow shedding.",
    },
    pricingNotes: "Strong demand from older housing stock. Short outdoor season means compressed scheduling. Lake effect weather experience required.",
    geo: { lat: 42.886, lng: -78.879 },
  },

  syracuse_ny: {
    slug: "syracuse-ny",
    region: "ny-upstate",
    terrain: "northeast",
    displayName: "Syracuse NY",
    state: "NY",
    county: "Onondaga County",
    licenseNote: "New York has no statewide GC license. Onondaga County / City of Syracuse permits required.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Syracuse", "Dewitt", "Fayetteville", "Manlius", "Camillus", "Liverpool", "Baldwinsville", "Clay"],
    buildingCodes: {
      decks: "Onondaga County: 50-75 psf snow load (heaviest in CONUS per capita). Frost depth 48\". Engineered plans required for complex decks. Permit required. Corrosion-resistant hardware only.",
      concrete: "Extreme freeze-thaw. 4500 psi air-entrained mandatory. Syracuse averages 127\" of snow/year — deicing chloride exposure is extreme.",
      painting: "No permits. Older housing stock with lead paint. Interior painting strong year-round.",
      roofing: "Syracuse: #1 snowiest city in the US per capita. Structural snow load engineering required. Metal roofing highly recommended. Full ice & water shield mandatory.",
    },
    pricingNotes: "Heavy snow market drives roofing repair demand. Interior trades strong year-round. Short outdoor season May-Oct.",
    geo: { lat: 43.048, lng: -76.147 },
  },

  // ─── OKLAHOMA ───
  tulsa: {
    slug: "tulsa",
    region: "ok-plains",
    terrain: "south-central",
    displayName: "Tulsa",
    state: "OK",
    county: "Tulsa County",
    licenseNote: "Oklahoma has no statewide GC license. Tulsa County / City permits required.",
    trades: ["decks", "concrete", "painting"], // roofing removed: license-required cell (hq#19)
    serviceArea: ["Tulsa", "Broken Arrow", "Bixby", "Jenks", "Owasso", "Sand Springs", "Claremore"],
    buildingCodes: {
      decks: "Tulsa County: 15-20 psf snow load. Frost depth 18-24\". Standard IRC deck permits. Tornado and wind zone — proper post anchoring and ledger bolting critical.",
      concrete: "Mild freeze-thaw. 3500-4000 psi. Clay soils with shrink-swell behavior — proper base and control joints essential.",
      painting: "No permits. Tulsa has extensive mid-century housing stock.",
      roofing: "Tornado alley! Wind 90-110 mph. Hail endemic. Class 4 impact resistant roofing strongly recommended — major insurance discount.",
    },
    pricingNotes: "Energy sector economy. Broken Arrow and South Tulsa suburbs command premium. Hail events drive roofing demand spikes.",
    geo: { lat: 36.154, lng: -95.993 },
  },

  oklahoma_city: {
    slug: "oklahoma-city",
    region: "ok-plains",
    terrain: "south-central",
    displayName: "Oklahoma City",
    state: "OK",
    county: "Oklahoma County",
    licenseNote: "Oklahoma has no statewide GC license. Oklahoma County / City of OKC permits required.",
    trades: ["decks", "concrete", "painting"], // roofing removed: license-required cell (hq#19)
    serviceArea: ["Oklahoma City", "Edmond", "Norman", "Moore", "Midwest City", "Yukon", "Mustang", "Del City"],
    buildingCodes: {
      decks: "Oklahoma County: 15-20 psf snow load. Frost depth 18-24\". Standard IRC deck permits. Wind and tornado zone — engineered fastening critical.",
      concrete: "Mild climate. 3500 psi. Expansive clay — proper base and joints essential. Red clay soil is notorious.",
      painting: "No permits. OKC has large suburban housing stock from 1960s-1990s build-out.",
      roofing: "Extreme tornado and hail zone. Wind 90-115 mph. Class 4 roofing is near-mandatory from insurance standpoint. Storm chaser fraud market — vet all leads.",
    },
    pricingNotes: "Large spread-out metro. Edmond and Norman are affluent suburbs. Roofing demand driven by regular hail events.",
    geo: { lat: 35.468, lng: -97.517 },
  },

  // ─── SOUTH DAKOTA ───
  sioux_falls: {
    slug: "sioux-falls",
    region: "ks-ne-plains",
    terrain: "plains",
    displayName: "Sioux Falls",
    state: "SD",
    county: "Minnehaha County",
    licenseNote: "South Dakota has no statewide GC license. Minnehaha County / City of Sioux Falls permits required.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Sioux Falls", "Brandon", "Harrisburg", "Tea", "Renner", "Crooks", "Baltic"],
    buildingCodes: {
      decks: "Minnehaha County: 30-40 psf snow load. Frost depth 42-48\". Permit required for decks >200 sqft or >30\" high. Wind: SD plains wind can be severe — proper anchoring required.",
      concrete: "Heavy freeze-thaw. 4000 psi air-entrained. Deep frost depth. Expansive Pierre shale soils in some areas.",
      painting: "No permits. Short exterior season.",
      roofing: "Minnehaha County: hail and wind. 30-40 psf snow. Wind 90-100 mph. Class 4 impact resistant recommended.",
    },
    pricingNotes: "Fast-growing market. No state income tax drives migration and renovation demand. Compressed outdoor build season May-Oct.",
    geo: { lat: 43.549, lng: -96.700 },
  },

  // ─── DELAWARE ───
  wilmington: {
    slug: "wilmington",
    region: "northeast-coastal",
    terrain: "northeast",
    displayName: "Wilmington",
    state: "DE",
    county: "New Castle County",
    licenseNote: "Delaware has no statewide GC license for residential work. New Castle County / City of Wilmington permits required.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Wilmington", "Newark", "Bear", "Middletown", "Glasgow", "Hockessin", "Pike Creek", "Claymont"],
    buildingCodes: {
      decks: "New Castle County: 15-25 psf snow load. Frost depth 24-30\". Standard IRC deck permits. Coastal areas near Delaware Bay may have flood zone considerations.",
      concrete: "Moderate freeze-thaw. 4000 psi air-entrained. Coastal salt exposure in eastern county.",
      painting: "No permits. Significant colonial-era housing stock with lead paint.",
      roofing: "Delaware coast: wind 90-100 mph. Hurricane/coastal storm exposure in eastern areas. Ice & water shield at eaves.",
    },
    pricingNotes: "Philadelphia suburban spillover market. Corporate headquarters environment (DuPont, financial sector) drives high-end renovation demand.",
    geo: { lat: 39.745, lng: -75.546 },
  },

  // ─── ILLINOIS ───
  peoria: {
    slug: "peoria",
    region: "il-great-lakes",
    terrain: "midwest",
    displayName: "Peoria IL",
    state: "IL",
    county: "Peoria County",
    licenseNote: "Illinois has no statewide GC license for residential work. City of Peoria / Peoria County permits required.",
    trades: ["decks", "concrete", "painting"], // roofing removed: license-required cell (hq#19)
    serviceArea: ["Peoria", "Pekin", "East Peoria", "Morton", "Washington", "Chillicothe", "Dunlap"],
    buildingCodes: {
      decks: "Peoria County: 20-25 psf snow load. Frost depth 36\". Permit required for decks >30\" or >200 sqft. Illinois River floodplain — check flood zone for riverside properties.",
      concrete: "Moderate-to-heavy freeze-thaw. 4000 psi air-entrained. Illinois clay soils — proper drainage and base.",
      painting: "No permits. Peoria has significant Caterpillar/manufacturing workforce housing stock.",
      roofing: "Peoria County: tornado and wind zone. Wind 90-100 mph. Hail active. Ice & water shield at eaves.",
    },
    pricingNotes: "Mid-Illinois market. Caterpillar/manufacturing employer base. Solid renovation demand at mid-market price points.",
    geo: { lat: 40.693, lng: -89.589 },
  },

  chicago_suburbs: {
    slug: "chicago-suburbs",
    region: "il-great-lakes",
    terrain: "midwest",
    displayName: "Chicago Suburbs",
    state: "IL",
    county: "DuPage / Will / Kane County",
    licenseNote: "Illinois has no statewide GC license for residential work. Suburb-specific village/city permits required.",
    trades: ["decks", "concrete", "painting"], // roofing removed: license-required cell (hq#19)
    serviceArea: ["Naperville", "Aurora", "Joliet", "Bolingbrook", "Elgin", "Schaumburg", "Oak Brook", "Downers Grove", "Wheaton", "Plainfield"],
    buildingCodes: {
      decks: "DuPage/Will/Kane County: 25-30 psf snow load. Frost depth 42\". Each municipality issues its own permits — verify with specific village. Cook County suburbs (unincorporated) have additional review. HOAs are very common and active.",
      concrete: "Heavy freeze-thaw. 4000-4500 psi air-entrained. Chicago clay soils. Deicing salt damage is prevalent — salt-resistant sealer required on driveways.",
      painting: "No statewide permit. Historic suburbs (Oak Park, Riverside, Hinsdale) may have historic preservation requirements.",
      roofing: "Chicago suburbs: Lake Michigan effect increases snow and wind. Wind 90-100 mph. Ice dam risk. Full ice & water shield. Impact-resistant for hail.",
    },
    pricingNotes: "Premium suburban market. Naperville, Hinsdale, Wheaton are affluent corridors. Competitive labor market. Material availability excellent.",
    geo: { lat: 41.785, lng: -88.147 },
  },

  // ─── TEXAS ───
  houston: {
    slug: "houston",
    region: "tx-gulf-coast",
    displayName: "Houston",
    state: "TX",
    county: "Harris County",
    licenseNote: "Texas has no statewide GC license. Houston requires only a Basic Business License (BBL, ~$33) — no GC exam, no contractor license. No state license. No city license verified as of 2026-04-14.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Houston", "Sugar Land", "Pearland", "Pasadena", "League City", "Friendswood", "Katy", "Baytown", "Missouri City", "Stafford"],
    buildingCodes: {
      decks: "TODO: Verify current Harris County / City of Houston deck permit thresholds and wind zone requirements. Houston area is Wind Zone II-III (130+ mph coastal). IBC/IRC adopted. Flood zone (FEMA) overlay applies in many neighborhoods — check elevation certificate requirements.",
      concrete: "TODO: Verify Harris County flatwork specs. Gulf Coast climate: no freeze-thaw cycle. Expansive Beaumont clay soils require proper subbase and moisture management. Thermal expansion control joints essential.",
      painting: "No permits required for residential painting. Extreme humidity and UV — exterior coatings must be mold/mildew-resistant and UV-stabilized.",
      roofing: "TODO: Verify Harris County roofing permit requirements and current wind uplift standards. Wind Zone II-III — hurricane-rated fastening required. Class A fire rating. Impact-resistant shingles strongly recommended for insurance savings.",
    },
    pricingNotes: "Largest metro in the platform. Gulf Coast humidity eliminates freeze-thaw but accelerates paint and coating degradation. Hurricane season drives roofing demand spikes. Expansive clay soils add complexity to concrete work. Competitive but deep labor pool.",
    geo: { lat: 29.760, lng: -95.370 },
  },

  dallas: {
    slug: "dallas",
    region: "tx-north-central",
    displayName: "Dallas",
    state: "TX",
    county: "Dallas County",
    licenseNote: "Texas has no statewide GC license. Dallas requires annual contractor registration ($120) with proof of GL insurance and TX sales tax permit — no exam, no license. No state license. No city license verified as of 2026-04-14.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Dallas", "Plano", "Garland", "Irving", "Grand Prairie", "McKinney", "Frisco", "Richardson", "Allen", "Carrollton"],
    buildingCodes: {
      decks: "TODO: Verify City of Dallas / Dallas County deck permit thresholds and current IRC adoption cycle. Blackland Prairie expansive clay soils cause significant footing heave — engineered footings often required. Wind 90 mph. Minimal snow load.",
      concrete: "TODO: Verify Dallas County flatwork specs. Blackland Prairie clay is among the most expansive soils in Texas — proper subbase, moisture management, and control joints are critical. No freeze-thaw. Thermal expansion joints for DFW summer heat.",
      painting: "No permits for residential painting. High UV and summer heat — premium exterior coatings required.",
      roofing: "TODO: Verify Dallas County roofing permit requirements. Active hail zone — Class 4 impact-resistant shingles provide significant insurance discounts. Wind 90+ mph. DFW is one of the highest-hail-frequency metros in the US.",
    },
    pricingNotes: "Nation's fastest-growing major metro. Elevated material lead times and labor competition. Blackland Prairie clay is a constant factor in every concrete and footing estimate. Hail events drive periodic roofing demand spikes across the entire DFW area.",
    geo: { lat: 32.776, lng: -96.797 },
  },

  san_antonio: {
    slug: "san-antonio",
    region: "tx-gulf-coast",
    displayName: "San Antonio",
    state: "TX",
    county: "Bexar County",
    licenseNote: "Texas has no statewide GC license. San Antonio requires registration with Development Services ($150/2yr) as Home Improvement or Residential Building Contractor — no exam, no license. No state license. No city license verified as of 2026-04-14.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["San Antonio", "New Braunfels", "Schertz", "Converse", "Universal City", "Boerne", "Helotes", "Leon Valley", "Live Oak", "Selma"],
    buildingCodes: {
      decks: "TODO: Verify Bexar County / City of San Antonio deck permit thresholds and current IBC/IRC cycle. Edwards Plateau limestone substrate in Hill Country areas affects footing design. Mild frost risk (frost depth ~12\"). Wind 90 mph.",
      concrete: "TODO: Verify Bexar County flatwork specs. Caliche and clay soils typical — no freeze-thaw but thermal expansion joints required for intense summer heat. Limestone bedrock in Hill Country fringe areas may affect depth of flatwork.",
      painting: "No permits for residential painting. High UV and summer heat — premium exterior coatings essential. Military base proximity (JBSA) drives large-scale painting contracts.",
      roofing: "TODO: Verify Bexar County roofing permit requirements. Hail zone with moderate frequency. Wind 90-100 mph. Not in coastal hurricane exposure zone. Class A fire rating required.",
    },
    pricingNotes: "Fastest-growing large TX market. Military presence (JBSA — Joint Base San Antonio) provides consistent renovation demand. Texas Hill Country limestone terrain adds complexity to concrete work. Summers are intense — exterior coatings must handle 100°F+ heat.",
    geo: { lat: 29.425, lng: -98.494 },
  },

  austin: {
    slug: "austin",
    region: "tx-north-central",
    displayName: "Austin",
    state: "TX",
    county: "Travis County",
    licenseNote: "Texas has no statewide GC license. Austin requires commercial contractor registration but no GC license for residential construction. No state license. No city license verified as of 2026-04-14.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Austin", "Round Rock", "Cedar Park", "Georgetown", "Pflugerville", "Leander", "Kyle", "Buda", "Hutto", "Manor"],
    buildingCodes: {
      decks: "TODO: Verify City of Austin deck permit thresholds under current 2021 IBC/IRC adoption. Expansive limestone and clay soils — engineered footings often required. Frost depth minimal (~12\"). Wind 90 mph. Austin's green building code may impose additional material requirements in certain zones.",
      concrete: "TODO: Verify Travis County flatwork specs. Caliche/limestone/clay soils require subbase engineering assessment. No freeze-thaw. Expansion joints critical for Austin's extreme summer heat cycles.",
      painting: "No permits for residential painting. Austin's 2021 energy code adoption may affect commercial exterior coating specs.",
      roofing: "TODO: Verify Travis County roofing permit requirements. Hail zone. Wind 90+ mph. Cool roofing products encouraged in Austin urban heat island zones. Class A fire rating. Wildland-urban interface (WUI) areas in western Travis County require fire-resistant roofing.",
    },
    pricingNotes: "Premium labor market — among the most expensive in Texas due to tech-sector wage competition. High-end renovation demand strong. Hill Country terrain commands premium for concrete work. Rapid growth creates material lead time pressure in peak season.",
    geo: { lat: 30.267, lng: -97.743 },
  },

  fort_worth: {
    slug: "fort-worth",
    region: "tx-north-central",
    displayName: "Fort Worth",
    state: "TX",
    county: "Tarrant County",
    licenseNote: "Texas has no statewide GC license. Fort Worth/Tarrant County follows the standard Texas contractor registration model — no exam, no license. No state license. No city license verified as of 2026-04-14.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Fort Worth", "Arlington", "Mansfield", "Euless", "Bedford", "Hurst", "Grapevine", "Colleyville", "Southlake", "Weatherford"],
    buildingCodes: {
      decks: "TODO: Verify Tarrant County / City of Fort Worth deck permit thresholds and current IRC adoption. Blackland Prairie clay soils cause significant footing heave — engineered footings often required. Wind 90+ mph. Minimal snow load.",
      concrete: "TODO: Verify Tarrant County flatwork specs. Blackland Prairie clay (shrink-swell soils) is critical factor in every flatwork estimate — moisture management, proper subbase, and control joint placement are essential. No freeze-thaw.",
      painting: "No permits for residential painting. High UV and summer heat — premium exterior coatings required.",
      roofing: "TODO: Verify Tarrant County roofing permit requirements. One of the most hail-prone counties in Texas. Class 4 impact-resistant shingles strongly recommended — provide significant insurance savings. Wind 90 mph.",
    },
    pricingNotes: "Western DFW serves a distinct Tarrant County market. Mid-range labor costs compared to Dallas proper. Blackland Prairie clay is the defining soil challenge — must be addressed in every concrete and deck footing estimate. Hail frequency is high.",
    geo: { lat: 32.755, lng: -97.330 },
  },

  el_paso: {
    slug: "el-paso",
    region: "tx-west-plains",
    displayName: "El Paso",
    state: "TX",
    county: "El Paso County",
    licenseNote: "Texas has no statewide GC license. El Paso requires contractor registration with bond and GL insurance via the city Planning & Inspections office — no exam, no license. No state license. No city license verified as of 2026-04-14.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["El Paso", "Socorro", "Horizon City", "Anthony TX", "Canutillo", "Clint", "Fabens", "Sunland Park NM"],
    buildingCodes: {
      decks: "TODO: Verify El Paso County deck permit thresholds and current IBC/IRC adoption. Desert climate: minimal frost risk (frost depth ~12\"). High wind zone 90+ mph. UV degradation is primary durability concern for decking materials.",
      concrete: "TODO: Verify El Paso County flatwork specs. Desert soils: low organic content, low moisture. Minimal freeze-thaw. Thermal expansion from extreme daily temperature swings — control joints critical. Caliche hardpan common — may require excavation.",
      painting: "No permits for residential painting. Extreme UV and heat — high-performance exterior coatings with UV inhibitors are essential. Fastest paint degradation environment on the platform.",
      roofing: "TODO: Verify El Paso County roofing permit requirements. High wind zone. Flat and low-slope roofing common in desert residential construction. Wind uplift-rated fastening critical. Extreme UV degrades roofing materials faster than most markets.",
    },
    pricingNotes: "Desert Southwest market. No freeze-thaw reduces concrete complexity but extreme UV and heat drive premium coating and roofing specifications. Military presence (Fort Bliss) provides steady renovation demand. Lower labor costs than most Texas metros.",
    geo: { lat: 31.759, lng: -106.487 },
  },

  // ─── OHIO (additional) ───
  cleveland: {
    slug: "cleveland",
    region: "oh-ky-in-rust-belt",
    displayName: "Cleveland",
    state: "OH",
    county: "Cuyahoga County",
    licenseNote: "Ohio has no statewide GC license. Cleveland city requires contractor registration with $25K bond and insurance certificate (no exam). Cuyahoga County suburbs each issue their own permits — no county-wide GC licensing exam. No state license. No city license (exam) verified as of 2026-04-14.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Cleveland", "Lakewood", "Parma", "Strongsville", "Westlake", "Avon", "Brook Park", "Euclid", "Cleveland Heights", "Solon"],
    buildingCodes: {
      decks: "TODO: Verify Cuyahoga County / City of Cleveland deck permit thresholds under current Ohio Building Code. Frost depth 36\". Snow load 25-30 psf (higher in eastern lake-effect belt). Corrosion-resistant hardware required near lakefront. Engineered plans typically required for elevated decks.",
      concrete: "TODO: Verify Cuyahoga County flatwork specs. Lake Erie freeze-thaw environment: 4000 psi air-entrained minimum. Salt exposure near lakefront accelerates deterioration — chloride-resistant mix recommended in eastern suburbs.",
      painting: "No permits for residential painting. Lake Erie humidity — exterior paint must be mold/mildew-resistant. Large stock of pre-1978 homes — lead paint disclosure required.",
      roofing: "TODO: Verify Cuyahoga County roofing permit requirements. Lake Erie snow belt: ice & water shield required at eaves. Snow load 25-30 psf. Wind 90 mph. Ice dam risk elevated in eastern suburbs.",
    },
    pricingNotes: "Mature housing stock — one of the oldest in the Midwest. Very strong demand for roofing replacement and exterior painting on aging homes. Lake Erie snow belt adds premium for roofing specs and compressed outdoor season. Mid-range labor costs.",
    geo: { lat: 41.499, lng: -81.695 },
  },

  // ─── INDIANA (additional) ───
  south_bend: {
    slug: "south-bend",
    region: "oh-ky-in-rust-belt",
    displayName: "South Bend",
    state: "IN",
    county: "St. Joseph County",
    licenseNote: "Indiana has no statewide GC license. South Bend / St. Joseph County requires contractor registration with $5K bond — no exam, no license. No state license. No city license verified as of 2026-04-14.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["South Bend", "Mishawaka", "Granger", "Elkhart", "Goshen", "Osceola", "Lakeville", "Niles MI", "Buchanan MI"],
    buildingCodes: {
      decks: "TODO: Verify St. Joseph County / City of South Bend deck permit thresholds under Indiana Residential Code. Frost depth 42\". Snow load 20-25 psf. Permits required for decks >200 sqft or >30\" above grade. Lake Michigan proximity increases precipitation.",
      concrete: "TODO: Verify St. Joseph County flatwork specs. Northern Indiana freeze-thaw: 4000 psi air-entrained minimum. Frost depth 42\" — significant footing depth requirement. Frost heave is a primary flatwork failure mechanism.",
      painting: "No permits for residential painting. Notre Dame area has significant historic housing stock.",
      roofing: "TODO: Verify St. Joseph County roofing permit requirements. Snow load 20-25 psf. Ice & water shield required at eaves. Wind 90 mph. Lake Michigan snow belt — above-average winter precipitation.",
    },
    pricingNotes: "University market (Notre Dame). Strong renovation demand from historic home stock near campus. Lake Michigan snow belt adds roofing premium. Mid-low labor costs. Serves northern Indiana and southwest Michigan border communities.",
    geo: { lat: 41.677, lng: -86.252 },
  },

  // ─── IOWA (additional) ───
  cedar_rapids: {
    slug: "cedar-rapids",
    region: "ia-mo-heartland",
    displayName: "Cedar Rapids",
    state: "IA",
    county: "Linn County",
    licenseNote: "Iowa has no statewide GC license. Contractors register with the Iowa Division of Labor ($50, administrative only, no exam). No city GC license in Cedar Rapids. No state license. No city license verified as of 2026-04-14.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Cedar Rapids", "Marion", "Hiawatha", "Robins", "Fairfax", "Ely", "North Liberty", "Iowa City", "Coralville"],
    buildingCodes: {
      decks: "TODO: Verify Linn County / City of Cedar Rapids deck permit thresholds under Iowa Building Code. Frost depth 42\". Snow load 25-30 psf. High wind zone (tornado alley edge) — proper post anchoring and ledger bolting required.",
      concrete: "TODO: Verify Linn County flatwork specs. Deep freeze-thaw: 4000 psi air-entrained minimum. Frost depth 42\" requires substantial footing depth. Iowa clay soils — proper gravel subbase essential.",
      painting: "No permits for residential painting. Lead paint disclosure for pre-1978 homes.",
      roofing: "TODO: Verify Linn County roofing permit requirements. Tornado zone — wind-rated fastening critical. Ice & water shield at eaves standard. Snow load 25-30 psf. Hail active in Iowa.",
    },
    pricingNotes: "Iowa's second city with stable manufacturing and healthcare economy. Mid-low labor costs. Severe weather (hail, tornado risk) drives periodic roofing demand spikes. Deep frost depth increases concrete and footing costs vs. southern markets.",
    geo: { lat: 41.977, lng: -91.666 },
  },

  // ─── NEW YORK (additional) ───
  albany: {
    slug: "albany",
    region: "ny-upstate",
    displayName: "Albany",
    state: "NY",
    county: "Albany County",
    licenseNote: "New York has no statewide GC license. NY State DOL contractor registry (effective Dec 30, 2024) is administrative registration only — $200 fee, no exam. HIC licensing applies only in NYC/Nassau/Suffolk/Westchester/Rockland/Putnam — Albany is outside that ring. No state license. No city license verified as of 2026-04-14.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Albany", "Troy", "Schenectady", "Saratoga Springs", "Colonie", "Latham", "Guilderland", "Bethlehem", "Clifton Park", "Rensselaer"],
    buildingCodes: {
      decks: "TODO: Verify Albany County / City of Albany deck permit thresholds under New York State Building Code. Frost depth 42\". Snow load 40-50 psf (Capital Region). Permits required for most deck projects. Engineered plans for elevated or complex decks.",
      concrete: "TODO: Verify Albany County flatwork specs. Significant freeze-thaw: 4000-4500 psi air-entrained concrete required. Frost depth 42\". Minimum 6\" slab for exterior flatwork in freeze exposure.",
      painting: "No permits for residential painting. Albany and Troy have extensive Victorian and early 20th-century housing stock — lead paint disclosure required. Historic district regulations may apply in downtown Albany and Troy.",
      roofing: "TODO: Verify Albany County roofing permit requirements. Snow load 40-50 psf — steep pitch residential roofing predominates. Ice & water shield at eaves required. Wind 90 mph. Ice dam risk elevated.",
    },
    pricingNotes: "State capital with stable government-sector economy. Large stock of Victorian and early 20th-century homes creates strong demand for roofing and exterior painting. Saratoga Springs provides a premium sub-market. Heavy snow loads require robust roofing specs. Mid-range labor costs.",
    geo: { lat: 42.652, lng: -73.757 },
  },

  // ─── WYOMING ───
  cheyenne: {
    slug: "cheyenne",
    region: "id-wy-intermountain",
    terrain: "plains",
    displayName: "Cheyenne",
    state: "WY",
    county: "Laramie County",
    licenseNote: "Wyoming has no statewide GC license. City of Cheyenne / Laramie County permits required.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Cheyenne", "Burns", "Pine Bluffs", "Albin", "Granite Canon"],
    buildingCodes: {
      decks: "Laramie County: 30-50 psf snow load (and extreme wind). Frost depth 42\". Permit required. Cheyenne is one of the windiest cities in the US — engineered wind bracing required on all elevated decks. Wind 100-130 mph design loads.",
      concrete: "Heavy freeze-thaw + extreme wind-driven moisture. 4500 psi air-entrained minimum. W/C ≤0.40. Exposed aggregate and textured finishes vulnerable to spalling.",
      painting: "No permits. High UV at elevation + extreme wind — premium exterior coatings essential. Short exterior season.",
      roofing: "Laramie County: one of the windiest locations in CONUS. Wind 90-130 mph. Impact-resistant roofing mandatory for insurance. Snow load + wind combination requires engineered fastening.",
    },
    pricingNotes: "Small but steady market. State capital and I-25/I-80 crossroads. Government and ranching economy.",
    geo: { lat: 41.140, lng: -104.820 },
  },

  // ─── COLORADO (Tier B additions) ───
  grand_junction: {
    slug: "grand-junction",
    region: "co-rocky-mountain",
    displayName: "Grand Junction",
    state: "CO",
    county: "Mesa County",
    licenseNote: "Colorado has no statewide GC license. Mesa County / City of Grand Junction permits required. No city GC exam.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Grand Junction", "Fruita", "Palisade", "Clifton", "Orchard Mesa", "Loma", "Battlement Mesa", "Rifle"],
    buildingCodes: {
      decks: "TODO: Verify Mesa County / City of Grand Junction deck permit thresholds and current IBC/IRC adoption. Western Slope desert climate: frost depth ~24\". Wind zone 90+ mph. UV degradation is the primary durability concern — composite and hardwood decking outperform pressure-treated in this high-UV, low-humidity environment.",
      concrete: "TODO: Verify Mesa County flatwork specs. Desert soil conditions: low organic content, caliche hardpan common — may require excavation for proper subbase. Minimal freeze-thaw vs. Front Range, but extreme daily thermal cycles in summer require control joints. No deicing salt exposure.",
      painting: "No permits for residential painting. Extreme UV at high altitude + desert dryness accelerates paint failure faster than Front Range markets. High-performance elastomeric coatings with UV inhibitors are essential. Exterior season is long (Mar–Nov).",
      roofing: "TODO: Verify Mesa County roofing permit requirements. High wind zone — wind uplift fastening critical. Flat and low-slope roofing common in desert residential construction. Minimal snow load vs. Front Range. UV degrades roofing materials faster than most CO markets.",
    },
    pricingNotes: "Western Colorado hub. Oil shale, agriculture, and outdoor recreation economy. Desert climate eliminates freeze-thaw concrete complexity but extreme UV drives premium coating and roofing specs. Labor market is thinner than Front Range — plan material and crew lead times accordingly.",
    geo: { lat: 39.064, lng: -108.550 },
  },

  pueblo: {
    slug: "pueblo",
    region: "co-rocky-mountain",
    displayName: "Pueblo",
    state: "CO",
    county: "Pueblo County",
    licenseNote: "Colorado has no statewide GC license. Pueblo County / City of Pueblo permits required. No city GC exam.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Pueblo", "Pueblo West", "Avondale", "Boone", "Canon City", "Florence", "Colorado City", "Walsenburg"],
    buildingCodes: {
      decks: "TODO: Verify Pueblo County / City of Pueblo deck permit thresholds under current Colorado Building Code. High plains climate: frost depth 24–30\". Snow load 20–30 psf (milder than Denver or the mountains). Wind 90+ mph. Hail zone — impact-resistant hardware recommended.",
      concrete: "TODO: Verify Pueblo County flatwork specs. Moderate freeze-thaw — milder than Denver. 4000 psi air-entrained minimum. Expansive soils in parts of Pueblo West — proper subbase critical. Same Colorado Building Code framework as Colorado Springs (on platform, 40 mi north).",
      painting: "No permits for residential painting. High UV at 4,700 ft elevation. Alkaline dust from surrounding high plains accelerates paint chalking — premium elastomeric recommended for stucco and masonry exteriors common in older Pueblo neighborhoods.",
      roofing: "TODO: Verify Pueblo County roofing permit requirements. Active hail zone — Class 4 impact-resistant shingles recommended. Wind 90+ mph. Snow load 20–30 psf. Ice & water shield at eaves standard. Same Colorado Building Code framework as Colorado Springs (on platform).",
    },
    pricingNotes: "Historic steel city with a blue-collar homeowner base and a large stock of older homes driving steady roofing and painting demand. Affordable labor market relative to Front Range. ~40 mi south of Colorado Springs (on platform) — same state licensing framework, strong operational overlap potential.",
    geo: { lat: 38.255, lng: -104.611 },
  },

  // ─── ILLINOIS (Tier A + B additions) ───
  rockford: {
    slug: "rockford",
    region: "il-great-lakes",
    displayName: "Rockford",
    state: "IL",
    county: "Winnebago County",
    licenseNote: "Illinois has no statewide GC license for residential work. Rockford requires contractor registration with a $10,000 surety bond — no GC exam. Verified against Rockford Building Department (2026). Roofing and plumbing contractors carry Illinois state licenses; general contractor registration is bond-and-insurance only.",
    trades: ["decks", "concrete", "painting"], // roofing removed: license-required cell (hq#19)
    serviceArea: ["Rockford", "Loves Park", "Machesney Park", "Belvidere", "Roscoe", "South Beloit", "Rockton", "Harlem", "Cherry Valley"],
    buildingCodes: {
      decks: "TODO: Verify Winnebago County / City of Rockford deck permit thresholds under Illinois Residential Code. Frost depth 42\". Snow load 25–30 psf. Permit required for decks >30\" above grade or >200 sqft. Tornado/wind zone — proper post anchoring and ledger bolting required.",
      concrete: "TODO: Verify Winnebago County flatwork specs. Heavy freeze-thaw: 4000 psi air-entrained minimum. Frost depth 42\" requires substantial footing depth. Illinois clay soils — proper gravel subbase essential. Deicing salt exposure from road salt common on driveways — salt-resistant sealer recommended.",
      painting: "No permits for residential painting. Rockford has a large stock of early 20th-century manufacturing-era homes — lead paint disclosure required for pre-1978 properties.",
      roofing: "TODO: Verify Winnebago County roofing permit requirements. Tornado and hail zone. Wind 90–100 mph. Ice & water shield at eaves standard. Snow load 25–30 psf. Same Illinois Building Code framework as Peoria (on platform).",
    },
    pricingNotes: "Illinois's second-largest city outside the Chicago orbit. Manufacturing and healthcare economy with a large stock of aging homes driving roofing, exterior painting, and renovation demand. Mid-low labor costs. Same state licensing framework as Peoria — minimal incremental compliance overhead.",
    geo: { lat: 42.271, lng: -89.094 },
  },

  champaign: {
    slug: "champaign",
    region: "il-great-lakes",
    displayName: "Champaign-Urbana",
    state: "IL",
    county: "Champaign County",
    licenseNote: "Illinois has no statewide GC license for residential work. City of Champaign / Champaign County permits required. No city GC exam — same framework as Peoria and Rockford (on platform).",
    trades: ["decks", "concrete", "painting"], // roofing removed: license-required cell (hq#19)
    serviceArea: ["Champaign", "Urbana", "Savoy", "Mahomet", "Rantoul", "Tolono", "St. Joseph", "Bondville"],
    buildingCodes: {
      decks: "TODO: Verify Champaign County / City of Champaign deck permit thresholds under Illinois Residential Code. Frost depth 42\". Snow load 20–25 psf. Permit required for decks over 30\" or 200 sqft. Tornado corridor — proper post anchoring and ledger connection required.",
      concrete: "TODO: Verify Champaign County flatwork specs. Moderate-to-heavy freeze-thaw: 4000 psi air-entrained minimum. Frost depth 42\". Prairie clay soils — proper gravel subbase and drainage critical. Flat terrain with poor natural drainage — slope and French drain planning important.",
      painting: "No permits for residential painting. University of Illinois area has significant pre-1978 rental housing stock — lead paint disclosure required.",
      roofing: "TODO: Verify Champaign County roofing permit requirements. Tornado alley — wind-rated fastening critical. Hail active in central Illinois. Wind 90–100 mph. Ice & water shield at eaves standard. Snow load 20–25 psf.",
    },
    pricingNotes: "University of Illinois market (60k+ campus community). Strong rental renovation demand from large student housing stock. Faculty and staff homeownership drives higher-end deck and painting projects. Same state licensing framework as Peoria — easy IL expansion.",
    geo: { lat: 40.117, lng: -88.244 },
  },

  springfield_il: {
    slug: "springfield-il",
    region: "il-great-lakes",
    displayName: "Springfield IL",
    state: "IL",
    county: "Sangamon County",
    licenseNote: "Illinois has no statewide GC license for residential work. City of Springfield / Sangamon County permits required. No city GC exam — same framework as Peoria, Rockford, and Champaign (on platform).",
    trades: ["decks", "concrete", "painting"], // roofing removed: license-required cell (hq#19)
    serviceArea: ["Springfield", "Rochester", "Sherman", "Chatham", "Auburn", "Riverton", "Williamsville", "Lincoln", "Decatur"],
    buildingCodes: {
      decks: "TODO: Verify Sangamon County / City of Springfield deck permit thresholds under Illinois Residential Code. Frost depth 36\". Snow load 20–25 psf. Permit required for most attached or elevated decks. Tornado corridor — proper fastening and ledger connection required.",
      concrete: "TODO: Verify Sangamon County flatwork specs. Moderate freeze-thaw: 4000 psi air-entrained minimum. Prairie clay soils standard for central Illinois — proper gravel subbase required.",
      painting: "No permits for residential painting. Springfield has a large stock of historic Victorian and Prairie-style homes — lead paint disclosure required for pre-1978 properties. Lincoln Home National Historic Site area has preservation guidelines.",
      roofing: "TODO: Verify Sangamon County roofing permit requirements. Tornado alley. Wind 90–100 mph. Hail active. Ice & water shield at eaves standard. Snow load 20–25 psf.",
    },
    pricingNotes: "Illinois state capital with stable government and healthcare economy. Older housing stock (Victorian through mid-century) creates strong roofing and exterior painting demand. Identical state licensing framework to Peoria (on platform) — minimal incremental compliance overhead.",
    geo: { lat: 39.798, lng: -89.654 },
  },

  bloomington_il: {
    slug: "bloomington-il",
    region: "il-great-lakes",
    displayName: "Bloomington-Normal IL",
    state: "IL",
    county: "McLean County",
    licenseNote: "Illinois has no statewide GC license for residential work. McLean County / City of Bloomington / Town of Normal permits required. No city GC exam — same Illinois framework as Peoria, Rockford, Champaign, and Springfield (on platform).",
    trades: ["decks", "concrete", "painting"], // roofing removed: license-required cell (hq#19)
    serviceArea: ["Bloomington", "Normal", "Lexington", "Pontiac", "Clinton", "Le Roy", "Chenoa", "Farmer City"],
    buildingCodes: {
      decks: "TODO: Verify McLean County / City of Bloomington deck permit thresholds under Illinois Residential Code. Frost depth 42\". Snow load 20–25 psf. Tornado corridor — proper post anchoring and ledger connection required.",
      concrete: "TODO: Verify McLean County flatwork specs. Moderate freeze-thaw: 4000 psi air-entrained minimum. Frost depth 42\". Prairie clay soils — proper gravel subbase and drainage critical.",
      painting: "No permits for residential painting. Bloomington-Normal has a mix of historic downtown housing and newer State Farm / ISU-adjacent suburbs. Lead paint disclosure for pre-1978 properties.",
      roofing: "TODO: Verify McLean County roofing permit requirements. Tornado and hail zone. Wind 90–100 mph. Ice & water shield at eaves standard. Snow load 20–25 psf.",
    },
    pricingNotes: "State Farm Insurance HQ + Illinois State University + Illinois Wesleyan University = stable professional homeowner base. Mid-range labor costs. ~50 miles east of Peoria (on platform) — same state framework, strong operational overlap potential.",
    geo: { lat: 40.485, lng: -88.994 },
  },

  // ─── IOWA (Tier B addition) ───
  waterloo: {
    slug: "waterloo",
    region: "ia-mo-heartland",
    displayName: "Waterloo / Cedar Falls",
    state: "IA",
    county: "Black Hawk County",
    licenseNote: "Iowa has no statewide GC license. Iowa Division of Labor contractor registration ($50, administrative only, no exam). No city GC license in Waterloo or Cedar Falls. Same Iowa framework as Des Moines and Cedar Rapids (on platform).",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Waterloo", "Cedar Falls", "Evansdale", "Hudson", "Waverly", "Independence", "Grundy Center", "Oelwein"],
    buildingCodes: {
      decks: "TODO: Verify Black Hawk County / City of Waterloo deck permit thresholds under Iowa Building Code. Frost depth 42\". Snow load 25–30 psf. High wind zone (tornado alley edge) — proper post anchoring and ledger bolting required. Permits required for decks >200 sqft or >30\" above grade.",
      concrete: "TODO: Verify Black Hawk County flatwork specs. Deep freeze-thaw: 4000 psi air-entrained minimum. Frost depth 42\" requires substantial footing depth. Iowa clay soils — proper gravel subbase essential. Cedar River floodplain areas — check flood zone status for flatwork near the river.",
      painting: "No permits for residential painting. Lead paint disclosure for pre-1978 homes. Waterloo has a significant stock of early 20th-century manufacturing-era housing.",
      roofing: "TODO: Verify Black Hawk County roofing permit requirements. Tornado and hail zone. Wind 90–100 mph. Ice & water shield at eaves standard. Snow load 25–30 psf. Same Iowa Building Code framework as Cedar Rapids (on platform, 60 mi south).",
    },
    pricingNotes: "Iowa's third MSA. Manufacturing and healthcare economy. Adding Waterloo/Cedar Falls completes Iowa's top-three markets alongside Des Moines and Cedar Rapids (both on platform). Mid-low labor costs. Strong hail and storm demand for roofing.",
    geo: { lat: 42.497, lng: -92.343 },
  },

  // ─── INDIANA (Tier B addition) ───
  bloomington_in: {
    slug: "bloomington-in",
    region: "oh-ky-in-rust-belt",
    displayName: "Bloomington IN",
    state: "IN",
    county: "Monroe County",
    licenseNote: "Indiana has no statewide GC license. Monroe County / City of Bloomington permits required. No city GC exam identified — same Indiana framework as Indianapolis, Fort Wayne, and South Bend (on platform).",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Bloomington", "Ellettsville", "Spencer", "Martinsville", "Bedford", "Nashville IN", "Morgantown"],
    buildingCodes: {
      decks: "TODO: Verify Monroe County / City of Bloomington deck permit thresholds under Indiana Residential Code. Frost depth 30\". Snow load 15–20 psf (milder than northern IN). Permits required for decks >30\" or >200 sqft. Tornado risk — proper anchoring required.",
      concrete: "TODO: Verify Monroe County flatwork specs. Southern Indiana has milder freeze-thaw than Fort Wayne or South Bend. 3500–4000 psi air-entrained recommended. Karst/limestone geology common in Monroe County — check for sinkhole zones in some areas before flatwork design.",
      painting: "No permits for residential painting. Bloomington has significant historic housing stock near Indiana University campus, plus a growing market of contemporary renovation for faculty and tech-sector homeowners.",
      roofing: "TODO: Verify Monroe County roofing permit requirements. Tornado risk — wind-rated fastening required. Wind 90 mph. Ice & water shield at eaves standard. Snow load 15–20 psf. Milder winter conditions than northern Indiana markets.",
    },
    pricingNotes: "Indiana University market (45k+ campus community). Historic housing stock near campus drives exterior painting and roofing demand. Karst limestone terrain adds complexity to concrete work in some Monroe County areas. Mid-range labor costs.",
    geo: { lat: 39.165, lng: -86.526 },
  },

  // ─── KENTUCKY (Tier B addition) ───
  bowling_green: {
    slug: "bowling-green",
    region: "oh-ky-in-rust-belt",
    displayName: "Bowling Green",
    state: "KY",
    county: "Warren County",
    licenseNote: "Kentucky has no statewide GC license for most trades. Warren County / City of Bowling Green permits required. No city GC exam identified — same Kentucky framework as Louisville and Lexington (on platform).",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Bowling Green", "Franklin", "Glasgow", "Scottsville", "Cave City", "Horse Cave", "Auburn", "Oakland"],
    buildingCodes: {
      decks: "TODO: Verify Warren County / City of Bowling Green deck permit thresholds under Kentucky Building Code. Frost depth 18–24\". Snow load 15–20 psf. Tornado risk — proper post anchoring and ledger connection required. Karst geology (Mammoth Cave region) — check for sinkhole zones before footing design in western Warren County.",
      concrete: "TODO: Verify Warren County flatwork specs. Mild freeze-thaw (similar to Louisville). 3500–4000 psi recommended. Karst limestone geology is a factor in western Warren County — some areas have active sinkhole risk requiring engineered foundations.",
      painting: "No permits for residential painting. Bowling Green has a growing mix of new suburban construction and older downtown housing stock. Western Kentucky University campus area has historic properties.",
      roofing: "TODO: Verify Warren County roofing permit requirements. Tornado alley edge. Wind 90 mph. Ice & water shield at eaves standard. Snow load 15–20 psf. Same Kentucky Building Code framework as Louisville and Lexington (on platform).",
    },
    pricingNotes: "Western Kentucky University + GM Corvette Assembly Plant = stable professional and blue-collar homeowner base. Growing market driven by I-65 manufacturing corridor. Same KY clean-licensing framework as Louisville/Lexington. Karst geology requires awareness for concrete and footing estimates in some areas.",
    geo: { lat: 36.990, lng: -86.443 },
  },

  // ─── MISSOURI (Tier A + B additions) ───
  columbia_mo: {
    slug: "columbia-mo",
    region: "ia-mo-heartland",
    displayName: "Columbia MO",
    state: "MO",
    county: "Boone County",
    licenseNote: "Missouri has no statewide GC license. City of Columbia / Boone County permits required. Note: Columbia has trade-specific exam boards for plumbing and electrical contractors — GC registration is permit-based only, no GC exam. Same general framework as Springfield MO and Kansas City MO (on platform).",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Columbia", "Ashland", "Centralia", "Fulton", "Hallsville", "Moberly", "Mexico MO"],
    buildingCodes: {
      decks: "TODO: Verify Boone County / City of Columbia deck permit thresholds under Missouri Building Code. Frost depth 24–30\". Snow load 15–20 psf. Tornado corridor — proper post anchoring and ledger connection required.",
      concrete: "TODO: Verify Boone County flatwork specs. Moderate freeze-thaw. 4000 psi air-entrained recommended. Missouri clay soils — proper gravel subbase required. Columbia sits on Pennsylvanian-age shale and limestone — subbase assessment important in some areas.",
      painting: "No permits for residential painting. Mizzou campus area has significant older housing stock — lead paint disclosure required for pre-1978 properties.",
      roofing: "TODO: Verify Boone County roofing permit requirements. Tornado alley. Wind 90–100 mph. Hail active in central Missouri. Ice & water shield at eaves standard. Snow load 15–20 psf.",
    },
    pricingNotes: "University of Missouri market (35k+ students). Stable government, education, and healthcare economy. Strong rental renovation demand from large student housing stock. Spring/summer decks and concrete are strong seasons. Same MO clean-licensing framework as Springfield MO (on platform).",
    geo: { lat: 38.952, lng: -92.334 },
  },

  joplin: {
    slug: "joplin",
    region: "ia-mo-heartland",
    displayName: "Joplin",
    state: "MO",
    county: "Jasper / Newton County",
    licenseNote: "Missouri has no statewide GC license. City of Joplin / Jasper and Newton County permits required. No city GC exam — same Missouri framework as Springfield MO and Kansas City MO (on platform).",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Joplin", "Carthage", "Carl Junction", "Webb City", "Neosho", "Granby", "Diamond", "Pittsburg KS", "Seneca"],
    buildingCodes: {
      decks: "TODO: Verify Jasper/Newton County / City of Joplin deck permit thresholds under Missouri Building Code. Frost depth 24\". Snow load 15–20 psf. EXTREME tornado zone — Joplin suffered a catastrophic EF5 tornado in 2011. Post-2011 rebuilt areas may have enhanced code adoption. Engineered wind fastening required.",
      concrete: "TODO: Verify Jasper/Newton County flatwork specs. Moderate freeze-thaw. 4000 psi air-entrained recommended. Ozarks limestone substrate in some areas — subbase prep and bedrock depth check important.",
      painting: "No permits for residential painting. Joplin's 2011 tornado rebuilding resulted in a large stock of 2011–2015 construction now approaching its first major repaint cycle — significant exterior painting demand.",
      roofing: "TODO: Verify Jasper/Newton County roofing permit requirements. Among the highest tornado/severe weather risk markets on the platform. Wind 90–110 mph design loads. Class 4 impact-resistant shingles strongly recommended. Joplin's insurance market heavily favors them post-2011.",
    },
    pricingNotes: "Ozarks tri-state hub (MO/KS/AR). The 2011 EF5 tornado reshaped the entire housing stock — a large cohort of 2011–2015 construction is now aging into its first major maintenance cycle, driving roofing and exterior painting demand. Adjacent to Springfield MO (on platform, ~70 mi east) — same MO licensing framework.",
    geo: { lat: 37.084, lng: -94.513 },
  },

  // ─── NEW YORK (Tier A additions) ───
  binghamton: {
    slug: "binghamton",
    region: "ny-upstate",
    displayName: "Binghamton",
    state: "NY",
    county: "Broome County",
    licenseNote: "New York has no statewide GC license. Broome County / City of Binghamton permits required. NY State DOL contractor registry (effective Dec 30, 2024): $200 administrative registration, no exam. HIC licensing applies only to NYC/Nassau/Suffolk/Westchester/Rockland/Putnam — Binghamton is outside that ring. Same NY framework as Rochester, Buffalo, Syracuse, and Albany (on platform).",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Binghamton", "Johnson City", "Endicott", "Vestal", "Endwell", "Owego", "Apalachin", "Conklin", "Kirkwood"],
    buildingCodes: {
      decks: "TODO: Verify Broome County / City of Binghamton deck permit thresholds under New York State Building Code. Frost depth 42\". Snow load 40–50 psf. Permits required for most deck projects. Engineered plans for elevated or complex decks. Broome County flood zone considerations along Susquehanna River (Hurricane Irene 2011 caused significant flooding).",
      concrete: "TODO: Verify Broome County flatwork specs. Heavy freeze-thaw: 4000–4500 psi air-entrained concrete required. Frost depth 42\". Susquehanna River valley — drainage and moisture management critical for flatwork in flood-adjacent areas.",
      painting: "No permits for residential painting. Binghamton and Endicott have extensive early 20th-century housing from the IBM and shoe manufacturing era — lead paint disclosure required for pre-1978 properties. Interior painting strong year-round given the compressed outdoor season.",
      roofing: "TODO: Verify Broome County roofing permit requirements. Snow load 40–50 psf. Ice & water shield at eaves required. Wind 90 mph. Ice dam risk. Same NY State Building Code framework as Rochester, Buffalo, and Syracuse (on platform).",
    },
    pricingNotes: "Triple Cities area (Binghamton/Endicott/Johnson City). Mature Rust Belt housing stock — IBM and manufacturing era homes from the 1920s–1950s dominate, creating strong roofing and exterior painting demand. Susquehanna River flood history means roofing repair cycles are shorter than normal. Mid-low labor costs.",
    geo: { lat: 42.099, lng: -75.918 },
  },

  utica: {
    slug: "utica",
    region: "ny-upstate",
    displayName: "Utica / Rome",
    state: "NY",
    county: "Oneida County",
    licenseNote: "New York has no statewide GC license. Oneida County / City of Utica / City of Rome permits required. NY State DOL contractor registry (effective Dec 30, 2024): $200 administrative registration, no exam. HIC licensing applies only to NYC/Nassau/Suffolk/Westchester/Rockland/Putnam — Utica/Rome is outside that ring. Same NY framework as Rochester, Buffalo, Syracuse, Albany, and Binghamton (on platform).",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Utica", "Rome", "New Hartford", "Whitesboro", "Oriskany", "Marcy", "Herkimer", "Little Falls", "Ilion"],
    buildingCodes: {
      decks: "TODO: Verify Oneida County / City of Utica deck permit thresholds under New York State Building Code. Frost depth 42–48\". Snow load 40–60 psf (some lake-effect influence from Lake Ontario). Permits required for most deck projects. Engineered plans for elevated designs.",
      concrete: "TODO: Verify Oneida County flatwork specs. Significant freeze-thaw: 4000–4500 psi air-entrained concrete required. Frost depth 42–48\". Minimum 6\" slab for exterior flatwork in this freeze exposure zone.",
      painting: "No permits for residential painting. Utica and Rome have a large stock of late 19th and early 20th-century housing from the textile and manufacturing era — lead paint disclosure required for pre-1978 properties. Interior painting strong year-round.",
      roofing: "TODO: Verify Oneida County roofing permit requirements. Snow load 40–60 psf with lake-effect influence from Lake Ontario. Full ice & water shield required. Wind 90 mph. Ice dam risk elevated. Same NY State Building Code framework as Buffalo and Syracuse (on platform).",
    },
    pricingNotes: "Mohawk Valley market. Mature manufacturing-era housing stock creates strong roofing and exterior painting demand. Completes the upstate NY corridor: Albany (on platform, ~90 mi east) and Syracuse (on platform, ~50 mi west). Griffiss Business and Technology Park drives some commercial renovation. Mid-low labor costs.",
    geo: { lat: 43.101, lng: -75.233 },
  },

  // ─── TEXAS (Tier A + B additions) ───
  corpus_christi: {
    slug: "corpus-christi",
    region: "tx-gulf-coast",
    displayName: "Corpus Christi",
    state: "TX",
    county: "Nueces County",
    licenseNote: "Texas has no statewide GC license. Corpus Christi requires contractor registration with proof of general liability insurance — no exam. No state license. TODO: Confirm current registration fee and bond requirement with City of Corpus Christi Building Services (361) 826-3240. TX pattern verified as of 2026-04-14.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Corpus Christi", "Portland TX", "Rockport", "Aransas Pass", "Ingleside", "Calallen", "Flour Bluff", "Padre Island", "Alice", "Robstown"],
    buildingCodes: {
      decks: "TODO: Verify Nueces County / City of Corpus Christi deck permit thresholds and current IBC/IRC adoption. Gulf Coast: Wind Zone III (130+ mph design winds per ASCE 7). All structural connections must meet hurricane-rated uplift requirements. Corrosion-resistant hardware mandatory — salt air accelerates fastener degradation. Composite decking strongly preferred over pressure-treated in coastal environment.",
      concrete: "TODO: Verify Nueces County flatwork specs. Gulf Coast climate: no freeze-thaw. Expansive coastal clay soils — moisture management and proper subbase critical. Thermal expansion control joints essential. Coastal salt environment: use Type V or sulfate-resistant cement mixes near tidal or brackish soil areas.",
      painting: "No permits for residential painting. Gulf Coast salt air and extreme humidity — exterior coatings must be mold/mildew-resistant, UV-stabilized, and salt-tolerant. Elastomeric coatings with moisture barrier properties strongly recommended. Annual exterior maintenance cycle common due to accelerated paint failure.",
      roofing: "TODO: Verify Nueces County roofing permit requirements and current wind uplift standards. Wind Zone III — hurricane-rated fastening, uplift-rated shingles, and sealed roof decks required. Class A fire rating. Corpus Christi is in the Texas hurricane strike zone — roofing specs must meet hurricane-rated standards where insurance requires it.",
    },
    pricingNotes: "Gulf Coast energy and military hub (Naval Air Station Corpus Christi). Hurricane-cycle roofing demand creates periodic large-volume surge opportunities. Salt air and coastal humidity accelerate all exterior material degradation — higher material specification than inland TX markets. Strong decks market driven by waterfront and bay-view properties.",
    geo: { lat: 27.800, lng: -97.396 },
  },

  killeen: {
    slug: "killeen",
    region: "tx-north-central",
    displayName: "Killeen / Fort Cavazos",
    state: "TX",
    county: "Bell County",
    licenseNote: "Texas has no statewide GC license. City of Killeen uses the MGO Connect permitting system for contractor registration — registration required, no exam. City of Temple: standard TX contractor registration model, no exam. No state license. TODO: Confirm current Killeen registration fee with Building Inspections Dept. TX pattern verified as of 2026-04-14.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Killeen", "Temple", "Fort Cavazos", "Harker Heights", "Copperas Cove", "Belton", "Nolanville", "Salado"],
    buildingCodes: {
      decks: "TODO: Verify Bell County / City of Killeen deck permit thresholds and current IBC/IRC adoption. Central Texas climate: frost depth ~12–18\". Minimal snow load. Wind 90 mph. Blackland Prairie expansive clay soils — engineered footings often required due to significant shrink-swell soil movement.",
      concrete: "TODO: Verify Bell County flatwork specs. Blackland Prairie expansive clay dominates the Killeen-Temple area — moisture management, proper subbase, and strategic control joint placement are critical. No freeze-thaw. Thermal expansion joints for intense central TX summer heat.",
      painting: "No permits for residential painting. Fort Cavazos proximity drives high-volume painting demand from unit turnovers and base housing renovation. Exterior coatings must handle 100°F+ summer heat and high UV.",
      roofing: "TODO: Verify Bell County roofing permit requirements. Active hail zone. Wind 90 mph. Class 4 impact-resistant shingles recommended. Fort Cavazos housing renovation creates steady roofing demand independent of storm cycles.",
    },
    pricingNotes: "Fort Cavazos (formerly Fort Hood, one of the largest US Army installations) drives relentless renovation demand from PCS (Permanent Change of Station) moves. Temple adds a stable healthcare economy (Baylor Scott & White). Blackland Prairie clay is the dominant site condition — must be addressed in every concrete and footing estimate.",
    geo: { lat: 31.117, lng: -97.729 },
  },

  beaumont: {
    slug: "beaumont",
    region: "tx-gulf-coast",
    displayName: "Beaumont / Port Arthur",
    state: "TX",
    county: "Jefferson County",
    licenseNote: "Texas has no statewide GC license. City of Beaumont requires contractor registration with a surety bond (coverage for 1 year from approval + 2 years post-final-inspection) — no exam. City of Port Arthur: standard TX contractor registration model. No state license. No city license (exam) verified as of 2026-04-14.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Beaumont", "Port Arthur", "Orange", "Nederland", "Groves", "Port Neches", "Lumberton", "Vidor", "Bridge City"],
    buildingCodes: {
      decks: "TODO: Verify Jefferson County / City of Beaumont deck permit thresholds and current IBC/IRC adoption. Gulf Coast: Wind Zone II-III. Hurricane-rated structural connections required. High humidity and salt proximity — corrosion-resistant hardware and composite or tropical hardwood decking preferred over pressure-treated.",
      concrete: "TODO: Verify Jefferson County flatwork specs. Gulf Coast: no freeze-thaw. Highly expansive Beaumont clay (gumbo clay) is among the most challenging soil conditions in Texas — significant shrink-swell requires thorough moisture management, proper subbase, and strategic control joint design. Post-tension slabs are common in this soil zone.",
      painting: "No permits for residential painting. Extreme Gulf Coast humidity (Southeast Texas averages 80%+ relative humidity) — exterior coatings must be mold/mildew-resistant, moisture-vapor-permeable, and UV-stabilized. Fastest paint degradation environment among Texas markets after Corpus Christi.",
      roofing: "TODO: Verify Jefferson County roofing permit requirements and hurricane wind uplift standards. Wind Zone II-III — hurricane-rated fastening and uplift-rated shingles required. Class A fire rating. Hurricane Harvey (2017) caused catastrophic roofing damage across Jefferson County — a second replacement cycle is now aging in. Impact-resistant roofing strongly recommended.",
    },
    pricingNotes: "Golden Triangle petrochemical hub (ExxonMobil, Chevron Phillips, DuPont). Refinery workforce has high renovation spending power. Hurricane Harvey (2017) drove a multi-year roofing/exterior repair cycle — a second cohort of post-storm roofing is now aging. Beaumont gumbo clay is among the most challenging soil conditions in the platform — must be addressed in every concrete and footing estimate.",
    geo: { lat: 30.086, lng: -94.102 },
  },

  lubbock: {
    slug: "lubbock",
    region: "tx-west-plains",
    displayName: "Lubbock",
    state: "TX",
    county: "Lubbock County",
    licenseNote: "Texas has no statewide GC license. Lubbock requires contractor registration with the City Building Inspection Department — $100,000 general liability insurance OR $20,000 compliance bond required; no exam. Verified against Lubbock City Code Article 28.04 (2026). No state license. No city license (exam) confirmed.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Lubbock", "Wolfforth", "Shallowater", "Slaton", "Levelland", "Idalou", "Tahoka", "Ransom Canyon"],
    buildingCodes: {
      decks: "TODO: Verify Lubbock County / City of Lubbock deck permit thresholds under current IBC/IRC adoption. High Plains climate: frost depth ~12–18\" (occasional hard freezes). Wind zone 90–100 mph (strong Panhandle winds). Minimal snow load. Caliche hardpan soil — augering footings may require special equipment in rocky caliche layers.",
      concrete: "TODO: Verify Lubbock County flatwork specs. Semi-arid High Plains: low moisture soils, caliche hardpan common. Minimal freeze-thaw, but significant daily thermal cycles require control joints. Caliche can be very hard — proper excavation and subbase preparation is the primary site challenge.",
      painting: "No permits for residential painting. High UV + strong Panhandle winds + low humidity — exterior coatings degrade faster than most TX markets. Premium exterior paints with UV inhibitors essential. Sand/dust abrasion from Panhandle winds is a secondary durability factor.",
      roofing: "TODO: Verify Lubbock County roofing permit requirements. Active hail zone — Lubbock sits in the southern hail belt. Class 4 impact-resistant shingles strongly recommended. Wind 90–100 mph with gusts significantly higher in Panhandle storm events.",
    },
    pricingNotes: "Texas Tech University market (40k+ students). Isolated High Plains hub — no adjacent platform market competes for this geography. Hail is the dominant roofing demand driver. Caliche hardpan adds a predictable site complexity to concrete and footing estimates. Confirmed clean licensing is a differentiator vs. markets still needing city-level verification.",
    geo: { lat: 33.578, lng: -101.855 },
  },

  waco: {
    slug: "waco",
    region: "tx-north-central",
    displayName: "Waco",
    state: "TX",
    county: "McLennan County",
    licenseNote: "Texas has no statewide GC license. City of Waco contractor registration follows the standard TX model (registration + bond/insurance, no exam). No state license. TODO: Confirm current Waco registration requirements with City of Waco Permits & Licenses — TX pattern strongly suggests no exam requirement.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Waco", "Woodway", "Hewitt", "Bellmead", "Lacy-Lakeview", "Lorena", "McGregor", "China Spring", "Hillsboro"],
    buildingCodes: {
      decks: "TODO: Verify McLennan County / City of Waco deck permit thresholds under current IBC/IRC adoption. Central Texas climate: frost depth ~12\". Minimal snow load. Wind 90 mph. Blackland Prairie expansive clay — engineered footings often required. Significant tornado risk in this corridor.",
      concrete: "TODO: Verify McLennan County flatwork specs. Blackland Prairie expansive clay (shrink-swell) dominates McLennan County — the same soil challenge as Dallas/Fort Worth. Moisture management, proper subbase, and control joint design are critical. No freeze-thaw. Thermal expansion joints required for Waco's intense summer heat.",
      painting: "No permits for residential painting. Waco's Magnolia/Fixer Upper renovation boom has created strong demand for quality exterior maintenance coatings on renovated historic homes. High UV and heat demand premium exterior products.",
      roofing: "TODO: Verify McLennan County roofing permit requirements. Active hail and tornado zone — Waco sits in a historically active tornado corridor. Wind 90+ mph. Class 4 impact-resistant shingles recommended. Tornado damage creates periodic roofing demand surges.",
    },
    pricingNotes: "The Magnolia/Chip and Joanna Gaines brand has made Waco renovation-conscious with national visibility. Baylor University (20k+ students) adds market stability. Midpoint between Dallas and Austin — supplies both labor and materials from two major metros. Blackland Prairie clay is the dominant site condition.",
    geo: { lat: 31.549, lng: -97.147 },
  },

  tyler: {
    slug: "tyler",
    region: "tx-north-central",
    displayName: "Tyler",
    state: "TX",
    county: "Smith County",
    licenseNote: "Texas has no statewide GC license. City of Tyler contractor registration follows the standard TX model (registration + bond/insurance, no exam). No state license. TODO: Confirm current Tyler registration requirements with City of Tyler Development Services — TX pattern strongly suggests no exam requirement.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Tyler", "Whitehouse", "Lindale", "Bullard", "Chandler", "Mineola", "Canton", "Palestine", "Jacksonville"],
    buildingCodes: {
      decks: "TODO: Verify Smith County / City of Tyler deck permit thresholds under current IBC/IRC adoption. East Texas climate: frost depth ~12\". Minimal snow load. Wind 90 mph. Sandy loam and red clay soils vary across Smith County — footing design requires site assessment.",
      concrete: "TODO: Verify Smith County flatwork specs. East Texas sandy loam and red clay soils — soil composition varies significantly. Sandy areas drain well but may require subbase compaction; clay areas need standard moisture management. No freeze-thaw. Thermal expansion joints for East Texas summer heat.",
      painting: "No permits for residential painting. Tyler's historic Azalea District and large stock of 1950s–1980s ranch homes drive strong exterior painting demand. High humidity in the Piney Woods — mold/mildew-resistant coatings essential.",
      roofing: "TODO: Verify Smith County roofing permit requirements. Active hail and tornado zone. Wind 90 mph. Class 4 impact-resistant shingles recommended. East Texas summer storm season generates consistent roofing demand.",
    },
    pricingNotes: "East Texas regional hub — dominant market for a wide rural geography with no adjacent UltraLife market. Tyler has a strong healthcare economy (UT Health East Texas) and a growing professional homeowner base. Pine forests and high humidity make exterior painting and roofing the highest-demand trades.",
    geo: { lat: 32.351, lng: -95.301 },
  },

  amarillo: {
    slug: "amarillo",
    region: "tx-west-plains",
    displayName: "Amarillo",
    state: "TX",
    county: "Potter / Randall County",
    licenseNote: "Texas has no statewide GC license. City of Amarillo uses the MGO Connect permitting system for contractor registration — standard TX model. No state license. TODO: Confirm current Amarillo registration fee and exam status with City of Amarillo Building Safety (806) 378-3041 — TX pattern strongly suggests no exam requirement.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Amarillo", "Canyon", "Bushland", "Tascosa", "Happy", "Claude", "Pampa", "Borger", "Dumas"],
    buildingCodes: {
      decks: "TODO: Verify Potter/Randall County / City of Amarillo deck permit thresholds under current IBC/IRC adoption. Texas Panhandle: frost depth ~18–24\" (harder freezes than south TX). Snow load 10–20 psf. EXTREME wind zone — Amarillo averages 13+ mph constant wind with gusts 60–80+ mph. All elevated decks require engineered wind bracing for Panhandle loads.",
      concrete: "TODO: Verify Amarillo area flatwork specs. Moderate freeze-thaw (harder than south TX, milder than northern states). 4000 psi air-entrained recommended. Caliche hardpan soils are the dominant site challenge — augering footings often requires specialized equipment. Extreme daily temperature swings drive thermal expansion cycles.",
      painting: "No permits for residential painting. Extreme UV at 3,600 ft elevation + strong abrasive Panhandle winds + low humidity — exterior coatings face accelerated degradation. Premium UV-stabilized exterior paints essential. Chalking and fading are the dominant failure modes.",
      roofing: "TODO: Verify Amarillo area roofing permit requirements. Among the most active hail markets in the US — Texas Panhandle sits in hail alley. Class 4 impact-resistant shingles near-mandatory from an insurance perspective. Wind 90–110 mph with Panhandle gust events exceeding those values. Roofing demand spikes after spring/summer hail events are reliable and large.",
    },
    pricingNotes: "Texas Panhandle hub. Extreme wind and hail make roofing the highest-demand trade — Class 4 impact-resistant roofing is essentially standard in this market. Caliche soils and deeper freeze (by TX standards) make concrete and footing estimates more complex than southern TX markets. Labor market is thinner than Dallas/Houston but consistent.",
    geo: { lat: 35.221, lng: -101.831 },
  },

  midland_odessa: {
    slug: "midland-odessa",
    region: "tx-west-plains",
    displayName: "Midland / Odessa",
    state: "TX",
    county: "Midland County / Ector County",
    // Combined entry for two adjacent MSAs that function as one Permian Basin market.
    // Midland (white-collar oil management) and Odessa (blue-collar oil services) are distinct
    // cities but share a single labor pool and customer geography. One platform config covers both.
    licenseNote: "Texas has no statewide GC license. Both Midland and Odessa follow the standard Texas contractor registration model — registration + bond/insurance, no exam. No state license. No city license (exam) verified as of 2026-04-14 for either city.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Midland", "Odessa", "Andrews", "Stanton", "Big Spring", "Monahans", "Pecos", "Crane"],
    buildingCodes: {
      decks: "TODO: Verify Midland County / City of Midland and Ector County / City of Odessa deck permit thresholds under current IBC/IRC adoption. Permian Basin desert climate: frost depth ~12\" (occasional hard freezes). Minimal snow load. Wind zone 90+ mph. Caliche hardpan soils — augering footings requires specialized equipment in many areas. UV degradation is the primary durability concern.",
      concrete: "TODO: Verify Midland/Ector County flatwork specs. Permian Basin desert: minimal freeze-thaw, but extreme daily thermal cycles — control joints are critical. Caliche hardpan requires excavation to achieve proper subbase. Low organic content soils drain well but moisture management still important for subgrade stability.",
      painting: "No permits for residential painting. Extreme UV at 2,800–3,000 ft elevation + desert dryness — exterior coatings degrade faster than most Texas markets. High-performance UV-stabilized paints essential. Boom-cycle construction produces high volumes of new home exterior painting demand.",
      roofing: "TODO: Verify Midland/Ector County roofing permit requirements. Active hail zone — Permian Basin hail season can be severe. Wind zone 90+ mph. Flat and low-slope roofing common in desert residential construction. UV degrades roofing materials faster than most markets.",
    },
    pricingNotes: "West Texas oil capital. Boom-bust oil economy creates volatile but high-value renovation cycles — during boom periods, homeowners spend heavily on upgrades. Decks and high-end painting see significant premium demand. Caliche soils and extreme UV define the primary site and material challenges. Combined Midland/Odessa population ~350k functions as one market.",
    geo: { lat: 31.970, lng: -102.075 },
  },

  bryan_college_station: {
    slug: "bryan-college-station",
    region: "tx-north-central",
    displayName: "Bryan / College Station",
    state: "TX",
    county: "Brazos County",
    // Two separate cities, one county, one market config. Bryan is the older blue-collar city;
    // College Station is the A&M university city. Register with each city separately.
    licenseNote: "Texas has no statewide GC license. City of Bryan and City of College Station each require contractor registration — standard TX model (registration + bond/insurance, no exam). No state license. TODO: Confirm registration requirements with City of Bryan Permits and City of College Station Development Services — TX pattern strongly suggests no exam requirement.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Bryan", "College Station", "Hearne", "Calvert", "Madisonville", "Brenham", "Huntsville", "Navasota"],
    buildingCodes: {
      decks: "TODO: Verify Brazos County / City of Bryan and City of College Station deck permit thresholds under current IBC/IRC adoption. East-central Texas climate: frost depth ~12\". Minimal snow load. Wind 90 mph. Expansive clay soils (Black Belt Prairie transition zone) — engineered footings may be required in areas with significant shrink-swell clay.",
      concrete: "TODO: Verify Brazos County flatwork specs. Transitional zone between Blackland Prairie clay (Bryan side) and East Texas sandy loam (rural Brazos County) — soil composition varies. Moisture management and proper subbase for clay areas. No freeze-thaw. Thermal expansion joints for intense central TX summer heat.",
      painting: "No permits for residential painting. Texas A&M campus area has significant older housing stock requiring exterior maintenance. Large volume of 1980s–2000s suburban construction approaching first major repaint cycle.",
      roofing: "TODO: Verify Brazos County roofing permit requirements. Active hail zone. Wind 90 mph. Class 4 impact-resistant shingles recommended. Gulf moisture occasionally brings heavy rain and wind events through the Brazos River corridor.",
    },
    pricingNotes: "Texas A&M University market (70k+ campus community — one of the largest in the US). Strong rental renovation demand plus a robust faculty and tech-sector homeowner base. Brazos County is growing rapidly as an Austin/Houston commuter and tech corridor. Two separate cities — register with each city individually.",
    geo: { lat: 30.627, lng: -96.334 },
  },

  abilene: {
    slug: "abilene",
    region: "tx-west-plains",
    displayName: "Abilene",
    state: "TX",
    county: "Taylor County",
    licenseNote: "Texas has no statewide GC license. City of Abilene contractor registration follows the standard TX model (registration + bond/insurance, no exam). No state license. TODO: Confirm current Abilene registration requirements with City of Abilene Development Services — TX pattern strongly suggests no exam requirement.",
    trades: ["decks", "concrete", "painting", "roofing"],
    serviceArea: ["Abilene", "Merkel", "Trent", "Clyde", "Wylie", "Buffalo Gap", "Tuscola", "Dyess AFB area"],
    buildingCodes: {
      decks: "TODO: Verify Taylor County / City of Abilene deck permit thresholds under current IBC/IRC adoption. West-central Texas climate: frost depth ~12–18\" (harder freezes than south TX). Minimal snow load. Wind zone 90+ mph. Caliche hardpan soils — footing augering may require specialized equipment.",
      concrete: "TODO: Verify Taylor County flatwork specs. West-central TX: moderate freeze-thaw (harder than south TX). Caliche hardpan soils similar to Lubbock and Amarillo. Proper excavation and subbase critical. Thermal expansion joints for intense summer heat cycles.",
      painting: "No permits for residential painting. High UV + low humidity + wind-driven dust — exterior coatings face accelerated degradation. Premium UV-stabilized paints essential. Dyess AFB housing renovation drives volume painting demand.",
      roofing: "TODO: Verify Taylor County roofing permit requirements. Active hail zone. Wind zone 90+ mph. Class 4 impact-resistant shingles recommended. Dyess AFB housing (1950s–1970s construction) creates steady roofing replacement demand.",
    },
    pricingNotes: "West Texas regional hub with military (Dyess AFB, 7th Bomb Wing) and three universities (Abilene Christian, Hardin-Simmons, McMurry). Military installation creates steady, predictable renovation demand. West Texas isolation means less labor competition than major metros. Caliche soils and high UV define the primary site and material challenges.",
    geo: { lat: 32.449, lng: -99.733 },
  },
};

// ─── MARKET LOOKUP HELPERS ───

/** Get market config by slug, returns null if not found */
export function getMarket(slug) {
  if (!slug) return null;
  // Normalize slug (replace spaces/underscores with hyphens)
  const normalized = slug.toLowerCase().replace(/[\s_]/g, "-");
  return Object.values(MARKETS).find(m => m.slug === normalized) || null;
}

/** Get all market slugs */
export function getAllSlugs() {
  return Object.values(MARKETS).map(m => m.slug);
}

/**
 * Find the nearest market to a lat/lng coordinate.
 * Uses the Haversine formula for accurate great-circle distance.
 */
export function getNearestMarket(lat, lng) {
  let nearest = null;
  let minDist = Infinity;

  const toRad = (deg) => deg * (Math.PI / 180);

  for (const market of Object.values(MARKETS)) {
    const dLat = toRad(market.geo.lat - lat);
    const dLng = toRad(market.geo.lng - lng);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat)) * Math.cos(toRad(market.geo.lat)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distMiles = 3959 * c; // Earth radius in miles

    if (distMiles < minDist) {
      minDist = distMiles;
      nearest = market;
    }
  }

  // Only return if within ~200 miles
  return minDist < 200 ? nearest : null;
}

/** Default market when none detected */
export const DEFAULT_MARKET = MARKETS.boise;

// ─────────────────────────────────────────────────────────────────────────────
// REGIONAL ARCHITECTURE
// Supplier-driven regional groupings for 61 markets across 19 states.
// Regions are defined by which lumber/concrete/roofing distributors actually
// serve each metro — operational supply-chain reality, not Census categories.
// Reference: content/regional-architecture/region-map.md
// ─────────────────────────────────────────────────────────────────────────────

export const REGIONS = {

  "tx-gulf-coast": {
    slug: "tx-gulf-coast",
    name: "Texas Gulf Coast",
    memberSlugs: ["houston", "beaumont", "corpus-christi", "san-antonio"],
    primaryLumber: ["Builders FirstSource", "McCoy's Building Supply", "84 Lumber"],
    primaryConcrete: ["CEMEX", "SRM Concrete (fmr. Vulcan TX)"],
    primaryRoofing: ["ABC Supply", "SRS Distribution / Southern Shingles"],
    pricingMultipliers: {
      concreteCYBaseline: 140,      // $/CY ready-mix baseline
      laborIndex: 0.85,             // relative to national baseline 1.0
      footingDepthIn: 18,           // frost depth inches
      airEntrainmentRequired: false,
      concreteMinPsi: 3000,
    },
    climateNotes: "Subtropical. No freeze-thaw. Hurricane season Jun-Nov. Full year-round build season; summer heat limits midday exterior work. High humidity accelerates paint degradation.",
    supplyChainNotes: "ABC Supply dominant (58+ TX locations). Hurricane landfall can exhaust regional shingle inventory in 72 hours. CEMEX has largest TX ready-mix footprint.",
    seasonalWindow: "year-round",
    riskNotes: "Hurricane surges can clear Houston ABC/SRS warehouses of shingles. Beaumont gumbo clay is among the most challenging soils on the platform.",
  },

  "tx-north-central": {
    slug: "tx-north-central",
    name: "North-Central Texas",
    memberSlugs: ["dallas", "fort-worth", "austin", "waco", "killeen", "bryan-college-station", "tyler"],
    primaryLumber: ["Builders FirstSource (Noltex truss hub)", "84 Lumber", "Sutherland's"],
    primaryConcrete: ["Martin Marietta / Quikrete (transition)", "SRM Concrete", "CEMEX"],
    primaryRoofing: ["ABC Supply", "SRS Distribution / D.J. Roofing Supply"],
    pricingMultipliers: {
      concreteCYBaseline: 135,
      laborIndex: 0.90,
      footingDepthIn: 12,
      airEntrainmentRequired: false,
      concreteMinPsi: 3000,
    },
    climateNotes: "Hot subtropical. Hail-prone spring (March-May). Winter ice storms rare but severe. Blackland Prairie expansive clay soils require engineered footings.",
    supplyChainNotes: "BFS Noltex truss plant anchors DFW/Austin supply. Martin Marietta divesting North TX ready-mix to Quikrete (closing 2025-2026). Storm chasers endemic.",
    seasonalWindow: "year-round",
    riskNotes: "Spring hail events drive simultaneous demand spikes across all 7 markets. DFW framing lumber can run tight in high-growth phases.",
  },

  "tx-west-plains": {
    slug: "tx-west-plains",
    name: "West Texas & Plains",
    memberSlugs: ["el-paso", "lubbock", "abilene", "midland-odessa", "amarillo"],
    primaryLumber: ["Builders FirstSource", "McCoy's Building Supply", "84 Lumber"],
    primaryConcrete: ["CEMEX (El Paso dominant)", "Martin Marietta / Quikrete", "local independents"],
    primaryRoofing: ["ABC Supply", "SRS Distribution"],
    pricingMultipliers: {
      concreteCYBaseline: 125,
      laborIndex: 0.80,
      footingDepthIn: 15,
      airEntrainmentRequired: false,
      concreteMinPsi: 3000,
    },
    climateNotes: "Arid to semi-arid. Minimal freeze-thaw. Strong wind (Amarillo/Lubbock). Extreme UV. Caliche hardpan is the primary concrete site challenge.",
    supplyChainNotes: "Thinner ABC/SRS branch density than Dallas/Houston. West TX is BFS-dependent. Midland-Odessa oil cycle creates volatile demand.",
    seasonalWindow: "year-round",
    riskNotes: "Midland-Odessa demand directly correlated to oil prices (30-50% swings). Single-distributor lumber risk in some isolated markets.",
  },

  "ok-plains": {
    slug: "ok-plains",
    name: "Oklahoma Plains",
    memberSlugs: ["oklahoma-city", "tulsa"],
    primaryLumber: ["Sutherland's (OK-native)", "84 Lumber", "Builders FirstSource"],
    primaryConcrete: ["Martin Marietta", "local/regional yards"],
    primaryRoofing: ["ABC Supply (expanding OK 2025)", "SRS Distribution / D.J. Roofing Supply"],
    pricingMultipliers: {
      concreteCYBaseline: 130,
      laborIndex: 0.82,
      footingDepthIn: 22,
      airEntrainmentRequired: true,
      concreteMinPsi: 3500,
    },
    climateNotes: "Tornado alley. Hail belt. Mild winters. Class 4 impact roofing is near-mandatory from insurance standpoint.",
    supplyChainNotes: "Storm chaser fraud is intense in OKC/Tulsa. Tornado events can exhaust regional shingle inventory within days.",
    seasonalWindow: "year-round",
    riskNotes: "Simultaneous tornado-driven demand can overwhelm regional shingle inventory. Some products need 2-3 day lead from TX warehouses.",
  },

  "ks-ne-plains": {
    slug: "ks-ne-plains",
    name: "Kansas-Nebraska Plains",
    memberSlugs: ["kansas-city-ks", "wichita", "omaha", "lincoln", "sioux-falls"],
    primaryLumber: ["Menards (9 KS + 14 NE + 1 SD stores)", "84 Lumber", "Builders FirstSource"],
    primaryConcrete: ["Martin Marietta", "Knife River / MDU Resources (upper plains)", "local independents"],
    primaryRoofing: ["ABC Supply", "SRS Distribution / D.J. Roofing Supply"],
    pricingMultipliers: {
      concreteCYBaseline: 145,
      laborIndex: 0.88,
      footingDepthIn: 40,
      airEntrainmentRequired: true,
      concreteMinPsi: 4000,
    },
    climateNotes: "Tornado/hail belt. Moderate-to-heavy freeze-thaw; frost depths 36-48in. Outdoor build season May-Oct in northern markets. Menards buying power keeps lumber pricing competitive.",
    supplyChainNotes: "SRS D.J. Roofing Supply covers MO/KS/OK/N.TX/S.NE/S.IA — one account covers this region + ia-mo-heartland (12 markets total). Sioux Falls is geographically isolated.",
    seasonalWindow: "May-October",
    riskNotes: "KC metro straddles KS/MO border — jurisdiction verification required per project address. Tornado damage can absorb regional roofing supply simultaneously.",
  },

  "ia-mo-heartland": {
    slug: "ia-mo-heartland",
    name: "Iowa-Missouri Heartland",
    memberSlugs: ["des-moines", "cedar-rapids", "waterloo", "kansas-city-mo", "springfield-mo", "columbia-mo", "joplin"],
    primaryLumber: ["Menards (22 IA + 21 MO stores)", "84 Lumber", "US LBM / Ridout (13 MO/AR locations)", "Meek's Lumber"],
    primaryConcrete: ["CRH / APAC Midwest", "Martin Marietta", "local co-ops (IA)"],
    primaryRoofing: ["ABC Supply", "SRS Distribution / D.J. Roofing Supply"],
    pricingMultipliers: {
      concreteCYBaseline: 138,
      laborIndex: 0.85,
      footingDepthIn: 36,
      airEntrainmentRequired: true,
      concreteMinPsi: 4000,
    },
    climateNotes: "Moderate-to-heavy freeze-thaw; frost depths 24-42in. Tornado season creates demand spikes. MO ice storms can ground exterior work for weeks Jan-Feb.",
    supplyChainNotes: "SRS D.J. Roofing Supply covers this region + ks-ne-plains (12 markets total). Menards dominates both IA and MO. Iowa 42in frost depth adds significant footing cost.",
    seasonalWindow: "April-November",
    riskNotes: "KC metro KS/MO split requires address-by-address jurisdiction check. Regional tornado events can strip roofing inventory across IA/MO/KS simultaneously.",
  },

  "oh-ky-in-rust-belt": {
    slug: "oh-ky-in-rust-belt",
    name: "Ohio-Kentucky-Indiana Rust Belt",
    memberSlugs: ["columbus", "cincinnati", "cleveland", "louisville", "lexington", "bowling-green", "indianapolis", "fort-wayne", "south-bend", "bloomington-in"],
    primaryLumber: ["84 Lumber (very strong OH)", "Carter Lumber (OH/KY/WV/IN regional)", "Menards (32 OH + 37 IN + 21 KY)", "US LBM / Standard (IN)"],
    primaryConcrete: ["CRH / Martin Marietta", "Irving Materials Inc. (IMI — IN dominant)", "Vulcan Materials (OH)"],
    primaryRoofing: ["ABC Supply", "Beacon / QXO"],
    pricingMultipliers: {
      concreteCYBaseline: 150,
      laborIndex: 0.92,
      footingDepthIn: 30,
      airEntrainmentRequired: true,
      concreteMinPsi: 4000,
    },
    climateNotes: "Moderate freeze-thaw (18-36in frost). Lake effect affects Cleveland, Fort Wayne, South Bend. Tornado risk in IN underrated. Cincinnati tri-state (OH/KY/IN) requires three permit jurisdictions.",
    supplyChainNotes: "Carter and 84 Lumber compete aggressively for OH/KY contractors. IMI dominates IN concrete. Cincinnati historic districts require specialty material sourcing.",
    seasonalWindow: "April-November",
    riskNotes: "Cleveland/Fort Wayne/South Bend lake effect causes sudden project shutdowns Oct-Mar. IMI single-supplier risk for IN concrete.",
  },

  "il-great-lakes": {
    slug: "il-great-lakes",
    name: "Illinois Great Lakes",
    memberSlugs: ["chicago-suburbs", "peoria", "rockford", "bloomington-il", "champaign", "springfield-il"],
    primaryLumber: ["Menards (60 IL stores — most of any state)", "84 Lumber", "US LBM"],
    primaryConcrete: ["CRH / Prairie Materials (Chicago metro dominant)", "Vulcan Materials (downstate IL)"],
    primaryRoofing: ["ABC Supply (Beloit WI HQ — IL is home territory)", "Beacon / QXO", "SRS / Midwest Roofing Supply (Joliet IL)"],
    pricingMultipliers: {
      concreteCYBaseline: 155,
      laborIndex: 0.95,
      footingDepthIn: 40,
      airEntrainmentRequired: true,
      concreteMinPsi: 4000,
    },
    climateNotes: "Heavy freeze-thaw; frost depth 36-42in. Chicago Suburbs: lake-effect snow and ice dam risk. Downstate IL: tornado alley. Deicing salt damage endemic. Exterior painting season May-Oct.",
    supplyChainNotes: "Menards dominant — best contractor lumber pricing in the network. ABC HQ in Beloit WI; IL is its backyard. CRH/Prairie Materials has near-monopoly on Chicago concrete.",
    seasonalWindow: "April-October",
    riskNotes: "Chicago Suburbs permit complexity: 30+ village permit offices. HOA approval adds 2-4 weeks in Naperville/Wheaton/Downers Grove.",
  },

  "co-rocky-mountain": {
    slug: "co-rocky-mountain",
    name: "Colorado Rocky Mountain",
    memberSlugs: ["colorado-springs", "fort-collins", "grand-junction", "pueblo"],
    primaryLumber: ["Builders FirstSource / Alpine Lumber (18 CO stores — acquired 2024)", "84 Lumber (expanding CO 2025)"],
    primaryConcrete: ["CEMEX (strong CO presence)", "Martin Marietta / Smyrna (CO ops divestiture in progress)"],
    primaryRoofing: ["ABC Supply", "Beacon / QXO"],
    pricingMultipliers: {
      concreteCYBaseline: 165,
      laborIndex: 1.00,
      footingDepthIn: 30,
      airEntrainmentRequired: true,
      concreteMinPsi: 4000,
    },
    climateNotes: "High-altitude severe freeze-thaw. Hail belt May-Aug. WUI fire zones (Class A roofing required). High UV at altitude degrades exterior paint faster. Grand Junction is high desert — different profile from Front Range.",
    supplyChainNotes: "BFS/Alpine consolidation (2024) creates near-single-distributor lumber risk in CO. Martin Marietta divesting CO ready-mix to Smyrna (transition ongoing).",
    seasonalWindow: "April-October",
    riskNotes: "WUI fire closures can halt projects during active build window with no notice. BFS/Alpine pricing may shift post-consolidation.",
  },

  "id-wy-intermountain": {
    slug: "id-wy-intermountain",
    name: "Idaho-Wyoming Intermountain",
    memberSlugs: ["boise", "cheyenne"],
    primaryLumber: ["Builders FirstSource (Boise)", "Menards (Cheyenne / WY)"],
    primaryConcrete: ["Aggregate Industries (Boise area)", "local Wyoming firms"],
    primaryRoofing: ["ABC Supply", "Beacon / QXO"],
    pricingMultipliers: {
      concreteCYBaseline: 155,
      laborIndex: 0.93,
      footingDepthIn: 33,           // avg: Boise 24in, Cheyenne 42in
      airEntrainmentRequired: true,
      concreteMinPsi: 4000,
    },
    climateNotes: "Note: use market-level overrides for precision. Boise: 24in frost, WUI fire risk, Treasure Valley clay. Cheyenne: 42in frost, 100-130 mph wind, heavy snow. Multipliers here are averages.",
    supplyChainNotes: "Two small markets paired by isolation. Boise uses BFS; Cheyenne uses Menards. Boise growth market: lumber tightens each spring. Cheyenne: 2-3 day lead time from Denver/SLC for some materials.",
    seasonalWindow: "May-October",
    riskNotes: "pricingMultipliers are averages — apply market-level overrides for precision. Cheyenne wind damage can generate demand while supply is constrained.",
  },

  "ny-upstate": {
    slug: "ny-upstate",
    name: "New York Upstate",
    memberSlugs: ["rochester-ny", "buffalo-ny", "syracuse-ny", "albany", "binghamton", "utica"],
    primaryLumber: ["Curtis Lumber (NY-native regional — dominant)", "US LBM / Amerhart (PA/NY/NE corridor)", "84 Lumber"],
    primaryConcrete: ["CRH / Callanan Industries (Albany/Hudson Valley dominant)", "CEMEX (Buffalo/Rochester)", "local independents"],
    primaryRoofing: ["Beacon / QXO (Northeast-dominant — primary)", "ABC Supply (present, secondary)"],
    pricingMultipliers: {
      concreteCYBaseline: 190,
      laborIndex: 1.10,
      footingDepthIn: 45,
      airEntrainmentRequired: true,
      concreteMinPsi: 4500,
    },
    climateNotes: "Heaviest lake-effect snow in CONUS. Buffalo/Rochester/Syracuse: 40-75+ psf design loads. Syracuse averages 127in snow/year. Short outdoor season May-Sept. Metal roofing premium demand. Ice dam remediation is a major secondary revenue stream.",
    supplyChainNotes: "Beacon/QXO is the primary roofing relationship across all 6 markets. Curtis Lumber regional dependency. Lake-effect snow can shut job sites for days with 48-hr notice.",
    seasonalWindow: "May-September",
    riskNotes: "Material lead times extend Oct-Mar. Short build season compresses scheduling — labor premium 20-30% in peak months.",
  },

  "northeast-coastal": {
    slug: "northeast-coastal",
    name: "Northeast Coastal",
    memberSlugs: ["portland-me", "manchester-nh", "wilmington"],
    primaryLumber: ["Hammond Lumber / US LBM (Maine dominant)", "US LBM (NH)", "84 Lumber (NH/DE)"],
    primaryConcrete: ["CRH / Redimix Companies (7 NH + S.Maine facilities — dominant)", "CRH / Tilcon (DE/NJ corridor)", "Eastern Concrete Materials (DE)"],
    primaryRoofing: ["Beacon / QXO (Northeast core territory — dominant)", "ABC Supply (secondary)"],
    pricingMultipliers: {
      concreteCYBaseline: 210,
      laborIndex: 1.20,
      footingDepthIn: 52,           // avg: ME/NH 48-60in, DE 24-30in
      airEntrainmentRequired: true,
      concreteMinPsi: 4500,
    },
    climateNotes: "ME/NH: 48-60in frost depth. Exterior work effectively May-Oct. Full ice & water shield is code. DE is mid-Atlantic: milder winters, coastal hurricane tail risk. Salt air (ME, DE) drives premium coating requirements.",
    supplyChainNotes: "ME: Hammond Lumber near-monopoly. CRH/Redimix near-monopoly in NH/ME concrete. Beacon/QXO is the primary roofing relationship for all 3 markets.",
    seasonalWindow: "May-October (ME/NH) / March-November (DE)",
    riskNotes: "Highest material and labor costs in the network. NH granite bedrock adds footing cost that must be quoted conservatively. DE is a single-market region — limited bulk purchasing leverage.",
  },
};

// ─── REGION HELPERS ───

/**
 * Get the REGIONS config for a given market object.
 * Returns null if the market has no region set or the region slug is unknown.
 * Backward-compatible: markets without a region field still work everywhere.
 *
 * @param {object} market  - Market config object from MARKETS
 * @returns {object|null}  - Region config, or null
 */
export function getRegion(market) {
  if (!market || !market.region) return null;
  return REGIONS[market.region] || null;
}

/**
 * Get all markets belonging to a given region slug.
 *
 * @param {string} regionSlug - e.g. "tx-gulf-coast"
 * @returns {object[]}        - Array of market config objects
 */
export function getMarketsByRegion(regionSlug) {
  if (!regionSlug) return [];
  return Object.values(MARKETS).filter(m => m.region === regionSlug);
}

/**
 * Get the region slug for a market slug string.
 * Convenience wrapper when you only have the slug, not the full market object.
 *
 * @param {string} marketSlug - e.g. "houston"
 * @returns {string|null}     - Region slug, or null
 */
export function getRegionSlug(marketSlug) {
  const market = getMarket(marketSlug);
  return market ? market.region || null : null;
}
